import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import type { SignatureModalValue } from '../components/signature/SignatureModal';
import type { PlanTermChoice } from '../domain/service';
import type { ProposalPresentation } from './installationProposals';
import { publicSupabase } from '../lib/supabaseClient';
import { getSupabaseClient } from './supabase';
import { readAllPages } from './readAllPages';
import { isMissingRelationError } from '../utils/supabaseErrors';

type ClientOption = { supabase?: SupabaseClient };

export type InstallationAccessMode = 'role_based' | 'admin_only' | 'full';
export type InstallationStatus =
  | 'draft'
  | 'approval_pending'
  | 'approved'
  | 'sent'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'lost'
  | 'cancelled';
export type InstallationQuoteStatus =
  | 'draft'
  | 'approval_pending'
  | 'approved'
  | 'sent'
  | 'accepted'
  | 'superseded';
export const INSTALLATION_LABOR_PURPOSES = ['General', 'Engineering', 'Installation', 'Project Management', 'Service', 'Training'];
export const installationPurposeChoices = (job: Pick<InstallationJobDetail, 'laborLines'>) => {
  const choices = new Map(INSTALLATION_LABOR_PURPOSES.map(purpose => [purpose.toLowerCase(), purpose]));
  job.laborLines.forEach(line => { const purpose = line.position.trim(); if (purpose && !choices.has(purpose.toLowerCase())) choices.set(purpose.toLowerCase(), purpose); });
  return [...choices.values()];
};

export type InstallationLaborUom = 'hourly' | 'half_day' | 'full_day';
export type InstallationLocationKind = 'client' | 'branch' | 'venue' | 'other';

export type InstallationAccessState = {
  proposalRoutingEnabled?: boolean;
  accessMode: InstallationAccessMode;
  canAccess: boolean;
  canSeeEvents: boolean;
  isSales: boolean;
  isLocation032: boolean;
};

export type InstallationListRow = {
  quoteType?: 'installation' | 'service_agreement';
  id: string;
  code: string;
  name: string;
  status: InstallationStatus;
  clientName: string | null;
  locationName: string | null;
  installationLocation: string | null;
  total: number;
  billedHours: number;
  updatedAt: string;
  scheduleUnlocked: boolean;
  clientId: string | null;
  clientBranchId: number | null;
  clientBranchName: string | null;
  venueId: string | null;
  venueName: string | null;
  requestedStartDate: string | null;
  deletedAt: string | null;
};

export type InstallationRoom = {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
};

export type InstallationQuoteLine = {
  id: string;
  lineageId?: string | null;
  roomId: string | null;
  lineType: 'item' | 'charge';
  catalogItemId: string | null;
  catalogChargeId: string | null;
  code: string | null;
  description: string;
  manufacturer: string | null;
  modelNumber: string | null;
  /** Who supplies it (Maintenance > Vendors). Staff only: never on a customer copy. */
  vendorId?: string | null;
  quantity: number;
  uom: string;
  unitPrice: number;
  unitCost?: number | null;
  marginPercent?: number | null;
  logisticsChargeType?: 'per_diem' | 'hotel' | 'airfare' | 'parking' | 'mileage' | 'shipping_handling' | 'other' | null;
  logisticsChargeDetails?: Record<string, unknown> | null;
  subtotal: number;
  notes: string | null;
  sortOrder: number;
  /** The items section this line sits under in its room, or null. */
  sectionId?: string | null;
  /** Hidden from the client but still billed (Alex, 2026-09-30). */
  nonPrintable?: boolean;
  /** Option lines only: the base line this one swaps, or null for a line the option adds. */
  replacesLineId?: string | null;
  /** The options this line belongs to; empty for a base line. */
  optionIds?: string[];
  /** On an accepted quote: the chosen option that brought this line in, if any. */
  appliedFrom?: InstallationAppliedLine | null;
};

/** A customer option (A-J) on a quote version. Its lines are option lines in the usual line arrays. */
export type InstallationQuoteOption = {
  id: string;
  lineageId: string | null;
  letter: string;
  name: string;
  description: string | null;
  recommended: boolean;
  sortOrder: number;
  /** The package group it belongs to (choose one, Phase 7); null for an any-mix add-on. */
  groupId: string | null;
};

/** The quote's packages (Good / Better / Best): options of which the customer signs exactly one. One per quote. */
export type InstallationQuoteOptionGroup = { id: string; name: string };

/** A heading over a room's items or labor. It groups lines and never prices. */
export type InstallationQuoteSection = {
  id: string;
  lineageId: string | null;
  roomId: string | null;
  grid: 'items' | 'labor';
  label: string;
  sortOrder: number;
  /** Show a subtotal row after the section's lines; it includes Non-Printable lines. */
  showSubtotal: boolean;
  /** One plain line the customer sees beside the heading in the proposal's Quote Detail (Phase 5); null when none. */
  customerSummary: string | null;
};

export type InstallationLaborLine = {
  id: string;
  lineageId?: string | null;
  roomId: string | null;
  roleId: string | null;
  position: string;
  workDate?: string | null;
  crewQuantity?: number;
  uomRateSnapshots?: Partial<Record<InstallationLaborUom, { rt: number; ot: number; dt: number }>>;
  uom: InstallationLaborUom;
  includedHoursSnapshot: number;
  rtUnits: number;
  otUnits: number;
  dtUnits: number;
  rtRate: number;
  otRate: number;
  dtRate: number;
  subtotal: number;
  billedHours: number;
  notes: string | null;
  sortOrder: number;
  /** The labor section this line sits under in its room, or null. */
  sectionId?: string | null;
  /** Hidden from the client but still billed (Alex, 2026-09-30). */
  nonPrintable?: boolean;
  /** Option lines only: the base line this one swaps, or null for a line the option adds. */
  replacesLineId?: string | null;
  /** The options this line belongs to; empty for a base line. */
  optionIds?: string[];
  /** On an accepted quote: the chosen option that brought this line in, if any. */
  appliedFrom?: InstallationAppliedLine | null;
};

export type InstallationQuoteVersion = {
  proposalSubmissionId?: string | null;
  proposalReviewState?: 'ready' | 'pending' | 'cleared' | 'rejected' | null;
  proposalApprovalCeiling?: number | null;
  originalRevisionOf?: string | null;
  confirmationMethod?: string | null;
  taxMode?: 'legacy' | 'automatic';
  taxRegionCode?: string | null;
  taxAsOf?: string;
  taxTotal?: number;
  taxContext?: { status?: string; region?: string; warnings?: string[]; base_tax?: number };
  id: string;
  versionNumber: number;
  status: InstallationQuoteStatus;
  itemTotal: number;
  laborTotal: number;
  warrantyTotal: number;
  total: number;
  billedHours: number;
  changeOrderId: string | null;
  acceptedAt: string | null;
  acceptedSignatureRecordId: string | null;
  /** Accepted with customer options (Phase 4): which were chosen and the lines they brought in. */
  appliedOptions?: InstallationAppliedOptions | null;
  createdAt: string;
};

/** A line a chosen option brought into the accepted quote: a swap (with what it replaced) or an add. */
export type InstallationAppliedLine = {
  grid: 'items' | 'labor';
  lineId: string;
  kind: 'swap' | 'add';
  /** The chosen options that held the line. */
  letters: string[];
  replaced?: { description: string; subtotal: number } | null;
};

/** What signing applied (installation_quote_versions.applied_options): every option, and the lines chosen. */
export type InstallationAppliedOptions = {
  options: Array<{ id: string; letter: string; name: string; chosen: boolean }>;
  lines: InstallationAppliedLine[];
};

export type InstallationWarrantyOption = {
  id: string;
  tierId: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  termMonths: number;
  pmVisitCount: number;
  discountPercent: number;
  responseLevel: string | null;
  onsiteSupport: string | null;
  manufacturerWarranty: string | null;
  loanerEquipment: string | null;
  included: boolean;
  sortOrder: number;
};

export type InstallationChangeOrder = {
  id: string;
  sequenceNumber: number;
  title: string;
  description: string | null;
  status: 'draft' | 'approval_pending' | 'approved' | 'sent' | 'accepted' | 'cancelled';
  baseTotal: number;
  revisedTotal: number;
  amountDelta: number;
  quoteVersionId: string | null;
  acceptedSignatureRecordId: string | null;
  createdAt: string;
  updatedAt: string;
};

export type InstallationApproval = {
  proposalSubmissionId?: string | null;
  id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  bandSequence: number | null;
  approverLabel: string | null;
  assignedProfileId: string | null;
  amountBasis: number;
  requestedAt: string;
  decidedAt: string | null;
  decisionNote: string | null;
};

export type InstallationContact = {
  id: string;
  clientContactId: string | null;
  roleLabel: string | null;
  notes: string | null;
  name: string;
  email: string | null;
  phone: string | null;
};

export type InstallationScheduleAssignment = {
  id: string;
  profileId: string | null;
  freelancerId: string | null;
  status: string;
  name: string;
};

export type InstallationScheduleShift = {
  laborPurpose?: string | null;
  workTypeParts?: Array<{ work_type: string | null; start_seconds: string; end_seconds: string | null }>;
  unpaidBreakMinutes?: number;
  updatedAt?: string;
  createdAt?: string | null;
  createdByName?: string | null;
  updatedByName?: string | null;
  id: string;
  roomId: string | null;
  laborLineId: string | null;
  laborLineageId?: string | null;
  roleId: string | null;
  startsAt: string;
  endsAt: string;
  requiredPeople: number;
  scheduleStatus: string;
  publishStatus: 'draft' | 'published';
  notes: string | null;
  assignments: InstallationScheduleAssignment[];
  /** Set once someone records time on the visit; the planned times are what it replaced. */
  timeEntryId?: string | null;
  plannedStartsAt?: string | null;
  plannedEndsAt?: string | null;
};

export type InstallationHourSummary = {
  laborLineId: string | null;
  position: string;
  billed: number;
  scheduled: number;
  planned: number;
  actual: number;
  remaining: number;
  over: number;
  unbilled: boolean;
};

export type InstallationJobDetail = {
  quoteType?: 'installation' | 'service_agreement';
  id: string;
  code: string;
  name: string;
  status: InstallationStatus;
  clientId: string | null;
  clientName: string | null;
  siteLocationId: string | null;
  locationName: string | null;
  primaryContactId: string | null;
  clientBranchId: number | null;
  clientBranchName: string | null;
  venueId: string | null;
  venueName: string | null;
  locationKind: InstallationLocationKind;
  salesProfileId: string | null;
  /** The Salesperson field's default when the job has none: the customer's salesperson, else the creator. */
  customerSalesProfileId: string | null;
  createdByProfileId: string | null;
  scopeOfWork: string;
  logisticsNotes: string;
  requestedStartDate: string | null;
  address: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  scheduleUnlocked: boolean;
  confirmedAt: string | null;
  version: number;
  updatedAt: string;
  updatedByName: string | null;
  rooms: InstallationRoom[];
  quote: InstallationQuoteVersion;
  inclusionsExclusions: string;
  selectedWarrantyOptionId: string | null;
  warrantyOptions: InstallationWarrantyOption[];
  quotes: InstallationQuoteVersion[];
  changeOrders: InstallationChangeOrder[];
  /** Base lines only: option lines never count in totals, Schedule, visits or pricing until signing. */
  quoteLines: InstallationQuoteLine[];
  laborLines: InstallationLaborLine[];
  /** Customer options on the current version (A-J), and their lines, kept apart from the base. */
  quoteOptions: InstallationQuoteOption[];
  /** The packages, when some options form a choose-one group (Phase 7). */
  optionGroup: InstallationQuoteOptionGroup | null;
  optionQuoteLines: InstallationQuoteLine[];
  optionLaborLines: InstallationLaborLine[];
  /** Whether customer options are turned on (installation_settings.quote_options_enabled). */
  optionsEnabled: boolean;
  /** The current version's section headings, every room and grid. */
  sections: InstallationQuoteSection[];
  approvals: InstallationApproval[];
  contacts: InstallationContact[];
  shifts: InstallationScheduleShift[];
  deletedAt: string | null;
};

/** What staff let a customer see (Customer view). The server applies these when it builds a snapshot. */
export type InstallationCustomerViewSettings = {
  pricing_detail: 'line_item' | 'section_totals' | 'summary_only';
  show_quantities: boolean;
  show_manufacturer_model: boolean;
  show_line_notes: boolean;
  itemize_labor: boolean;
  /** Each labor line's crew: position, crew size and hours each (since 2026-10-26; absent on older copies). */
  show_labor_detail?: boolean;
  show_options: boolean;
  allow_online_acceptance: boolean;
  allow_customer_responses: boolean;
};

/** One position behind a labor line on the customer's copy: "3 × Installation · 4 hrs each". */
export type InstallationCustomerCrew = { position: string; crew_quantity: number; hours_each: number };

export type InstallationCustomerViewState = {
  settings: InstallationCustomerViewSettings;
  /** Whose settings apply: the installation's own, its location's defaults, or the system's. */
  source: 'installation' | 'location' | 'default';
  /** The installation's own row revision; 0 when it has none. Saves send it back. */
  revision: number;
  updated_at?: string | null;
  location_default: InstallationCustomerViewSettings;
  location_name?: string | null;
};

export type InstallationCustomerViewDefaults = {
  location_id: string;
  location_name?: string | null;
  settings: InstallationCustomerViewSettings;
  revision: number;
  customized: boolean;
};

export type InstallationCustomerQuoteSnapshot = {
  quote_type?: 'installation' | 'service_agreement';
  agreement_locations?: import('./installationAgreements').AgreementCustomerLocation[];
  /** 2 since the Customer view allowlist (2026-10-07); older stored snapshots have none. */
  snapshot_version?: number;
  /** The settings the snapshot was built with; absent on older stored snapshots, which show everything. */
  view_settings?: InstallationCustomerViewSettings;
  job?: { job_code?: string; name?: string; scope_of_work?: string | null; client_name?: string | null; installation_address?: string | null };
  rooms?: Array<{ installation_room_id: string; name: string; description?: string | null }>;
  /** Each room's billed amount, hidden lines included; empty when only the quote total is shown. */
  room_totals?: Array<{ installation_room_id: string | null; items_subtotal: number; labor_subtotal: number; subtotal: number }>;
  quote?: {
    version?: number;
    base_total?: number;
    base_tax?: number | null;
    tax_total?: number;
    item_total?: number;
    labor_total?: number;
    /** Shipping & Handling, a total of its own (since 2026-10-26): in base_total and item_total, not in the lines. */
    shipping_handling?: number;
    billed_hours?: number;
    selected_warranty_option_id?: string | null;
    /** Signed for longer than the plan's standard term (since 2026-10-13); absent for the standard term. */
    selected_warranty_term_years?: number;
    /** A signed copy's totals for the customer's choices. */
    warranty_total?: number;
    total?: number;
  };
  /**
   * Signed copies only (since 2026-10-07): what the customer chose and the total they accepted. With
   * options, their names and what they added (tax included). An Admin Confirm copy is the quote as
   * confirmed, so it lists no options; `option_names` says which were chosen.
   */
  acceptance?: {
    choices?: Partial<InstallationQuoteChoices>;
    agreement_name?: string | null;
    total?: number;
    option_names?: string[];
    options_total?: number;
    confirmed_without_signature?: boolean;
  };
  /** Customer options, when Customer view shows them (absent on a copy without options). */
  options?: InstallationCustomerQuoteOption[];
  /** The package groups (Phase 7), only on a copy with packages: the customer signs exactly one of each. */
  option_groups?: Array<{ id: string; name: string; kind: 'choose_one' }>;
  /**
   * Each option line's amount over the base, tax included, listed once however many options hold it. Staff
   * copies only: the signing page doesn't get these, and totals a mix with the server's terms instead.
   */
  option_line_amounts?: Array<{ line_id: string; grid: 'items' | 'labor'; option_ids: string[]; replaces_line_id: string | null; delta: number }>;
  /**
   * Since 2026-10-01: headings with at least one printable line, each with a subtotal that includes
   * its Non-Printable lines. Older snapshots have none.
   */
  sections?: Array<{
    installation_quote_section_id: string;
    installation_room_id?: string | null;
    grid: 'items' | 'labor';
    label: string;
    sort_order: number;
    show_subtotal: boolean;
    /** Absent when the customer sees only the quote total. */
    subtotal?: number;
    /** Phase 5: present only on a heading with a summary. */
    customer_summary?: string;
  }>;
  /** Keys the settings hide are absent, not null: amounts, quantities, models and notes. */
  lines?: Array<{
    installation_quote_line_id: string;
    installation_room_id?: string | null;
    installation_quote_section_id?: string | null;
    notes?: string | null;
    description: string;
    manufacturer_snapshot?: string | null;
    model_number_snapshot?: string | null;
    quantity?: number;
    uom?: string;
    unit_price?: number;
    subtotal?: number;
  }>;
  /** Itemized lines, or one "Installation labor" line per room (no line id) when labor is not itemized. */
  labor?: Array<{
    installation_labor_line_id?: string;
    installation_room_id?: string | null;
    installation_quote_section_id?: string | null;
    notes?: string | null;
    position: string;
    crew_quantity?: number;
    uom?: string;
    rt_units?: number;
    ot_units?: number;
    dt_units?: number;
    billed_hours?: number;
    subtotal?: number;
    /** When Customer view shows labor crew detail: the line's own crew, or every crew behind a room's one line. */
    crew?: InstallationCustomerCrew[];
  }>;
  warranty_options?: Array<{
    installation_quote_warranty_option_id: string;
    name_snapshot: string;
    description_snapshot?: string | null;
    price_snapshot: number;
    tax_amount?: number;
    coverage_start?: string;
    coverage_end?: string;
    quoted_years?: number;
    term_mode?: 'years' | 'dates';
    term_months_snapshot: number;
    pm_visit_count_snapshot: number;
    parts_labor_discount_percent_snapshot: number;
    included_by_default?: boolean;
    /** The plan's tier code and comparison details as frozen with the quote (absent on older copies). */
    code_snapshot?: string;
    response_level_snapshot?: string | null;
    onsite_support_snapshot?: string | null;
    manufacturer_warranty_snapshot?: string | null;
    loaner_equipment_snapshot?: string | null;
    /**
     * A paid plan that can run longer (since 2026-10-13): every length it can be signed for, standard first,
     * with the server's price and tax. Absent when the plan can't be extended.
     */
    term_choices?: PlanTermChoice[];
  }>;
  inclusions_exclusions?: string | null;
};

export type InstallationSnapshotSection = NonNullable<InstallationCustomerQuoteSnapshot['sections']>[number];

/**
 * An option on the customer's copy: its price over the base quote (tax included; negative when it lowers
 * the price) and the lines it swaps in or adds, with only the details Customer view shows. Choosing several
 * options counts a line they share once, so their total comes from the server's terms, not these prices.
 */
export type InstallationCustomerQuoteOption = {
  installation_quote_option_id: string;
  letter: string;
  name: string;
  description?: string | null;
  recommended?: boolean;
  price: number;
  lines: InstallationCustomerQuoteOptionLine[];
  /** A package (Phase 7): the choose-one group it belongs to; absent on an any-mix add-on. */
  group_id?: string;
};

export type InstallationCustomerQuoteOptionLine = {
  grid: 'items' | 'labor';
  line_id: string;
  /** A swap replaces a base line (`replaces_line_id`); an add joins the quote. */
  kind: 'swap' | 'add';
  replaces_line_id: string | null;
  installation_room_id?: string | null;
  installation_quote_section_id?: string | null;
  sort_order?: number;
  /** The item, or the labor position ("Installation labor" when labor is not itemized). */
  description: string;
  replaces_description?: string | null;
  manufacturer_snapshot?: string | null;
  model_number_snapshot?: string | null;
  quantity?: number;
  uom?: string;
  unit_price?: number;
  crew_quantity?: number;
  rt_units?: number;
  ot_units?: number;
  dt_units?: number;
  billed_hours?: number;
  /** The line's own subtotal, when the copy shows line amounts. */
  subtotal?: number;
  /** What choosing the line changes the room and heading subtotals by, before tax (when the copy shows them). */
  subtotal_change?: number;
  /** Labor: what it changes the billed hours by (when the copy shows quantities). */
  billed_hours_change?: number;
  /** Labor, when Customer view shows labor crew detail. */
  crew?: InstallationCustomerCrew[];
  notes?: string | null;
};

export type InstallationCustomerQuotePayload = ProposalPresentation & {
  job_code?: string;
  name?: string;
  recipient_email?: string | null;
  quote?: InstallationCustomerQuoteSnapshot;
  /**
   * Client View only: `signed` (the accepted copy), `sent` (the copy the customer's link shows),
   * `live` (not sent yet) or `draft` (the live quote with unsaved settings).
   */
  source?: 'signed' | 'sent' | 'live' | 'draft';
  status?: string;
  /** Sent only: whether the live quote, or its settings, changed since it was sent. */
  live_differs?: boolean;
  /** Sent only (Phase 3): whether today's proposal words, customer or salesperson differ from the sent ones. */
  presentation_differs?: boolean;
  sent_at?: string | null;
  expires_at?: string | null;
};

/**
 * What a customer chooses before signing: the service agreement and, when the copy offers options, the
 * options (`option_ids`, possibly empty). The server's terms echo the choices back in letter order.
 * `warranty_term_years` extends a paid plan past its standard term (at most 3 years in total); the terms
 * echo it only when it is longer than the standard term.
 */
export type InstallationQuoteChoices = { warranty_option_id: string | null; option_ids?: string[]; warranty_term_years?: number | null; agreement_selections?: import('./installationAgreements').AgreementSelections };

/**
 * A signing link's state. `signed`: this link was used, and it shows the signed copy until it expires.
 * `accepted`: the quote was signed another way. `withdrawn`: it went back for edits, a newer version
 * exists or the job closed. `replaced`: a newer link was sent.
 */
export type InstallationQuoteLinkState = 'open' | 'signed' | 'accepted' | 'declined' | 'replaced' | 'withdrawn' | 'expired';
export type InstallationQuoteResponseType = 'decline' | 'request_changes';
export type InstallationQuoteDeclineReason = 'budget' | 'timing' | 'other_company' | 'on_hold' | 'other';

export type InstallationQuoteLinkResponse = {
  response_type: InstallationQuoteResponseType;
  decline_reason: InstallationQuoteDeclineReason | null;
  message: string | null;
  responder_name: string;
  responded_at: string;
};

/**
 * What the signing page reads. Only an open link carries its copy; a signed one carries the signed copy. Since
 * Phase 3 every state also carries the proposal words, customer and salesperson frozen with the link.
 */
export type InstallationQuoteLink = ProposalPresentation & {
  state: InstallationQuoteLinkState;
  job_code: string;
  name: string;
  sent_at: string;
  expires_at: string;
  /** The installation's sales rep, else whoever sent the link. */
  contact_name: string | null;
  recipient_email?: string | null;
  quote?: InstallationCustomerQuoteSnapshot | null;
  can_sign?: boolean;
  can_respond?: boolean;
  responses?: InstallationQuoteLinkResponse[];
  signature?: InstallationQuoteLinkSignature;
};

export type InstallationQuoteLinkSignature = {
  signer_name: string;
  signed_at: string;
  acceptance_statement: string;
  signature_method: string;
  signature_image_data_url: string | null;
};

/** The server's terms for a choice: the statement the signature dialog shows, and the totals behind it. */
export type InstallationQuoteTerms = {
  choices: InstallationQuoteChoices;
  agreement_name: string | null;
  /** How long the chosen plan runs, in years (null for the included coverage; absent before 2026-10-13). */
  agreement_years?: number | null;
  version: number | null;
  base_total: number;
  agreement_price: number;
  tax_total: number;
  total: number;
  statement: string;
  /** The chosen options ("Option A · Name") and what they add, tax included (Phase 4; absent before). */
  option_names?: string[];
  options_total?: number;
  /** In person only: the copy the terms were built from. The acceptance is refused if it changed. */
  snapshot_hash?: string;
  quote_version_id?: string;
  /** In person only: the copy's options, for the choices dialog. */
  options?: InstallationCustomerQuoteOption[];
};

export type InstallationQuoteLinksSummary = {
  links: Array<{
    installation_signature_request_id: string;
    quote_version_id: string;
    version: number;
    recipient_email: string | null;
    sent_at: string;
    sent_by: string | null;
    expires_at: string;
    used_at: string | null;
    closed_at: string | null;
    state: InstallationQuoteLinkState;
    /** Set when staff emailed the link (Phase 5); absent before that migration. */
    recipient_name?: string | null;
    email_status?: 'sending' | 'sent' | 'failed' | null;
    emailed_at?: string | null;
  }>;
  responses: Array<{
    installation_quote_response_id: string;
    installation_signature_request_id: string;
    quote_version_id: string;
    response_type: InstallationQuoteResponseType;
    decline_reason: InstallationQuoteDeclineReason | null;
    message: string | null;
    responder_name: string;
    responder_email: string | null;
    choices: Partial<InstallationQuoteChoices>;
    quoted_total: number | null;
    responded_at: string;
  }>;
};

export type InstallationQuoteResponseInput = {
  type: InstallationQuoteResponseType;
  declineReason: InstallationQuoteDeclineReason | null;
  message: string;
  /** Request changes only: the customer's selections go with the message. */
  choices: InstallationQuoteChoices | null;
  name: string;
  email: string;
};

/** What the response dialog collects; the page adds the customer's current choices. */
export type InstallationQuoteResponseDraft = Omit<InstallationQuoteResponseInput, 'choices'>;

export const INSTALLATION_DECLINE_REASONS: ReadonlyArray<{ value: InstallationQuoteDeclineReason; label: string }> = [
  { value: 'budget', label: 'Over our budget' },
  { value: 'timing', label: 'Timing doesn’t work' },
  { value: 'other_company', label: 'Going with another company' },
  { value: 'on_hold', label: 'Project is on hold or canceled' },
  { value: 'other', label: 'Something else' },
];

export type InstallationInvoiceState = {
  invoiceEnabled: boolean;
  canManage: boolean;
  netsuiteStatus: string;
  invoiceId: string | null;
  docNo: string | null;
  invoiceStatus: string | null;
  queueStatus: string;
  issueDate: string | null;
  total: number;
};

export type InstallationCatalogRecord = {
  id: string;
  kind: 'item' | 'charge';
  code: string | null;
  name: string;
  manufacturer: string | null;
  modelNumber: string | null;
  description: string | null;
  defaultUom: string;
  defaultUnitPrice: number;
  taxable: boolean | null;
  active: boolean;
  revenueGroup: string | null;
  sellGlCode: string | null;
  netsuiteItemInternalId: string | null;
  netsuiteItemExternalId: string | null;
  usageCount: number;
  updatedAt: string;
};

export type InstallationWizardOption = { id: string; label: string; detail?: string | null };
export type InstallationCatalogOption = InstallationWizardOption & {
  code: string | null;
  manufacturer: string | null;
  modelNumber: string | null;
  uom: string;
  unitPrice: number;
  kind: 'item' | 'charge';
};
export type InstallationLaborRoleOption = InstallationWizardOption & {
  code: string | null;
  rates: Record<InstallationLaborUom, { rt: number; ot: number; dt: number }>;
};

export type InstallationCreatePayload = {
  quote_type?: 'installation' | 'service_agreement';
  name: string;
  client_id: string;
  primary_contact_id: string;
  client_branch_id: number | null;
  venue_id: string | null;
  installation_location_kind: InstallationLocationKind;
  requested_start_date: string | null;
  installation_address_line1: string;
  installation_address_line2: string;
  installation_city: string;
  installation_state: string;
  installation_postal_code: string;
  installation_country: string;
};

const numberValue = (value: unknown): number => Number(value ?? 0) || 0;
const objectValue = <T extends object>(value: unknown): T => (value && typeof value === 'object' ? value as T : {} as T);

export async function fetchInstallationAccessState(options?: ClientOption): Promise<InstallationAccessState> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('installation_access_state');
  if (error) throw error;
  const row = objectValue<Record<string, unknown>>(data);
  return {
    proposalRoutingEnabled: row.proposal_routing_enabled === true,
    accessMode: row.access_mode === 'role_based'
      ? 'role_based'
      : row.access_mode === 'full'
        ? 'full'
        : 'admin_only',
    canAccess: row.can_access === true,
    canSeeEvents: row.can_see_events !== false,
    isSales: row.is_sales === true,
    isLocation032: row.is_location_032 === true,
  };
}

export async function updateInstallationAccessMode(mode: InstallationAccessMode, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('installation_update_access_mode', { p_access_mode: mode });
  if (error) throw error;
}

export async function fetchInstallationWizardOptions(options?: ClientOption): Promise<{
  locations: InstallationWizardOption[];
  catalog: InstallationCatalogOption[];
  laborRoles: InstallationLaborRoleOption[];
  warrantyTiers: Array<{ id: string; code: string; name: string; description: string | null; price: number; termMonths: number; pmVisitCount: number; discountPercent: number; responseLevel: string | null }>;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const [locations, items, charges, rates, warrantyTiersResult] = await Promise.all([
    supabase.from('locations').select('location_id,name,siteid').eq('active', true).order('name'),
    supabase.from('installation_catalog_items').select('*').eq('active', true).order('name'),
    supabase.from('installation_catalog_charges').select('*').eq('active', true).order('name'),
    supabase.rpc('installation_labor_role_rates'),
    supabase.from('service_agreement_tiers').select('tier_id,code,name,description,price,term_months,pm_visit_count,parts_labor_discount_percent,response_level,sort_order').eq('active', true).order('sort_order'),
  ]);
  const failure = [locations, items, charges, rates, warrantyTiersResult].find(result => result.error)?.error;
  if (failure) throw failure;
  const rateRows = (rates.data ?? []) as any[];
  const roleRows = new Map<string, any>();
  rateRows.forEach(row => {
    const id = String(row.labor_role_id ?? '');
    if (id && !roleRows.has(id)) roleRows.set(id, row);
  });
  const laborRoles: InstallationLaborRoleOption[] = Array.from(roleRows.values()).map(row => {
    const resolvedRates = (uom: InstallationLaborUom) => {
      const match = rateRows.find(rate => rate.labor_role_id === row.labor_role_id && rate.uom === uom);
      return { rt: numberValue(match?.rate_rt), ot: numberValue(match?.rate_ot), dt: numberValue(match?.rate_dt) };
    };
    return {
      id: String(row.labor_role_id),
      label: String(row.role_name),
      code: row.role_code ? String(row.role_code) : null,
      rates: { hourly: resolvedRates('hourly'), half_day: resolvedRates('half_day'), full_day: resolvedRates('full_day') },
    };
  });
  const mapCatalog = (row: any, kind: 'item' | 'charge'): InstallationCatalogOption => ({
    id: String(kind === 'item' ? row.installation_catalog_item_id : row.installation_catalog_charge_id),
    label: String(row.name),
    detail: [row.manufacturer, row.model_number].filter(Boolean).join(' · ') || null,
    code: row.item_code ?? row.charge_code ?? null,
    manufacturer: row.manufacturer ?? null,
    modelNumber: row.model_number ?? null,
    uom: row.default_uom ?? 'each',
    unitPrice: numberValue(row.default_unit_price),
    kind,
  });
  return {
    locations: ((locations.data ?? []) as any[]).map(row => ({ id: String(row.location_id), label: row.name || row.siteid || 'Unnamed location', detail: row.siteid ?? null })),
    catalog: [
      ...((items.data ?? []) as any[]).map(row => mapCatalog(row, 'item')),
      ...((charges.data ?? []) as any[]).map(row => mapCatalog(row, 'charge')),
    ],
    laborRoles,
    warrantyTiers: ((warrantyTiersResult.data ?? []) as any[]).map(row => ({
      id: String(row.tier_id), code: String(row.code), name: String(row.name), description: row.description ?? null,
      price: numberValue(row.price), termMonths: numberValue(row.term_months), pmVisitCount: numberValue(row.pm_visit_count),
      discountPercent: numberValue(row.parts_labor_discount_percent), responseLevel: row.response_level ?? null,
    })),
  };
}

export async function fetchClientContacts(clientId: string, options?: ClientOption): Promise<InstallationWizardOption[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('client_contacts')
    .select('id,first_name,last_name,email,title')
    .eq('client_id', clientId)
    .is('deleted_at', null)
    .order('last_name');
  if (error) throw error;
  return ((data ?? []) as any[]).map(row => ({
    id: String(row.id),
    label: [row.first_name, row.last_name].filter(Boolean).join(' ') || row.email || 'Unnamed contact',
    detail: [row.title, row.email].filter(Boolean).join(' · ') || null,
  }));
}

export async function createInstallationJob(payload: InstallationCreatePayload, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('create_installation_job_intake', { p_payload: payload });
  if (error) throw error;
  if (!data) throw new Error('Installation job creation did not return an id.');
  return String(data);
}

export type InstallationListFilters = {
  clientId?: string | null;
  venueId?: string | null;
  includeArchived?: boolean;
  archivedOnly?: boolean;
};

export async function fetchInstallations(filters: InstallationListFilters = {}, options?: ClientOption): Promise<InstallationListRow[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  let request = supabase
    .from('installation_jobs')
    .select(`installation_job_id,quote_type,job_code,name,status,client_id,client_branch_id,venue_id,requested_start_date,schedule_unlocked,updated_at,deleted_at,installation_address_line1,installation_address_line2,installation_city,installation_state,installation_postal_code,installation_country,
      clients(business_name),client_branches(branch_name),venues(venue_name),locations(name),installation_quote_versions(total,billed_hours,version_number)`)
    .order('job_code', { ascending: false });
  if (filters.clientId) request = request.eq('client_id', filters.clientId);
  if (filters.venueId) request = request.eq('venue_id', filters.venueId);
  if (filters.archivedOnly) request = request.not('deleted_at', 'is', null);
  else if (!filters.includeArchived) request = request.is('deleted_at', null);
  const { data, error } = await request;
  if (error) throw error;
  return ((data ?? []) as any[]).map(row => {
    const quotes = Array.isArray(row.installation_quote_versions) ? row.installation_quote_versions : [];
    const quote = [...quotes].sort((a, b) => numberValue(b.version_number) - numberValue(a.version_number))[0];
    return {
      quoteType: row.quote_type === 'service_agreement' ? 'service_agreement' as const : 'installation' as const,
      id: String(row.installation_job_id), code: String(row.job_code), name: String(row.name),
      status: row.status as InstallationStatus, clientName: row.clients?.business_name ?? null,
      locationName: row.locations?.name ?? null,
      installationLocation: formatInstallationAddress(row),
      total: numberValue(quote?.total), billedHours: numberValue(quote?.billed_hours),
      updatedAt: String(row.updated_at), scheduleUnlocked: row.schedule_unlocked === true,
      clientId: row.client_id ?? null,
      clientBranchId: row.client_branch_id == null ? null : Number(row.client_branch_id),
      clientBranchName: row.client_branches?.branch_name ?? null,
      venueId: row.venue_id ?? null,
      venueName: row.venues?.venue_name ?? null,
      requestedStartDate: row.requested_start_date ?? null,
      deletedAt: row.deleted_at ?? null,
    };
  }).sort((a, b) => {
    // Imported jobs retain their DSO chronology, regardless of import/edit time.
    const aDso = /^DSO\d+$/i.test(a.code);
    const bDso = /^DSO\d+$/i.test(b.code);
    if (aDso !== bDso) return aDso ? -1 : 1;
    return b.code.localeCompare(a.code, 'en', { numeric: true, sensitivity: 'base' })
      || a.id.localeCompare(b.id);
  });
}

function formatInstallationAddress(row: Record<string, unknown>): string | null {
  const cityState = [row.installation_city, row.installation_state].filter(Boolean).join(', ');
  const cityLine = [cityState, row.installation_postal_code].filter(Boolean).join(' ');
  const address = [row.installation_address_line1, row.installation_address_line2, cityLine, row.installation_country]
    .filter(Boolean)
    .join(', ');
  return address || null;
}

function mapQuoteLine(row: any): InstallationQuoteLine {
  return {
    id: String(row.installation_quote_line_id), lineageId: row.lineage_id ?? null, roomId: row.installation_room_id ?? null,
    lineType: row.line_type === 'charge' ? 'charge' : 'item',
    catalogItemId: row.installation_catalog_item_id ?? null, catalogChargeId: row.installation_catalog_charge_id ?? null,
    code: row.code_snapshot ?? null, description: row.description ?? '', manufacturer: row.manufacturer_snapshot ?? null,
    modelNumber: row.model_number_snapshot ?? null, vendorId: row.vendor_id ?? null, quantity: numberValue(row.quantity), uom: row.uom ?? 'each',
    logisticsChargeType: row.logistics_charge_type ?? null, logisticsChargeDetails: row.logistics_charge_details ?? null,
    unitCost: row.unit_cost == null ? null : Number(row.unit_cost),
    marginPercent: row.margin_percent == null ? null : Number(row.margin_percent),
    unitPrice: numberValue(row.unit_price), subtotal: numberValue(row.subtotal), notes: row.notes ?? null,
    sortOrder: numberValue(row.sort_order),
    sectionId: row.installation_quote_section_id ?? null, nonPrintable: row.non_printable === true,
    replacesLineId: row.replaces_line_id ?? null, optionIds: [],
  };
}

type InstallationQuoteOptionRow = {
  installation_quote_option_id: string; lineage_id: string | null; letter: string; name: string | null;
  description: string | null; recommended: boolean | null; sort_order: number | null;
  /** Absent before 20261014172600. */
  installation_quote_option_group_id?: string | null;
};
type InstallationOptionMembershipRow = { installation_quote_option_id: string; installation_quote_line_id?: string; installation_labor_line_id?: string };

function mapQuoteOption(row: InstallationQuoteOptionRow): InstallationQuoteOption {
  return {
    id: String(row.installation_quote_option_id), lineageId: row.lineage_id ?? null, letter: String(row.letter),
    name: String(row.name ?? ''), description: row.description ?? null, recommended: row.recommended === true,
    sortOrder: numberValue(row.sort_order), groupId: row.installation_quote_option_group_id ?? null,
  };
}

type InstallationQuoteSectionRow = {
  installation_quote_section_id: string; lineage_id: string | null; installation_room_id: string | null;
  grid: string; label: string | null; sort_order: number | null; show_subtotal: boolean | null;
  /** Absent before 20261014172400. */
  customer_summary?: string | null;
};

function mapSection(row: InstallationQuoteSectionRow): InstallationQuoteSection {
  return {
    id: String(row.installation_quote_section_id), lineageId: row.lineage_id ?? null, roomId: row.installation_room_id ?? null,
    grid: row.grid === 'labor' ? 'labor' : 'items', label: row.label ?? '', sortOrder: numberValue(row.sort_order),
    showSubtotal: row.show_subtotal === true, customerSummary: row.customer_summary?.trim() || null,
  };
}

function mapLaborLine(row: any): InstallationLaborLine {
  return {
    id: String(row.installation_labor_line_id), roomId: row.installation_room_id ?? null,
    lineageId: row.lineage_id ?? null,
    roleId: row.labor_role_id ?? null, position: row.position ?? '', uom: row.uom as InstallationLaborUom,
    workDate: row.work_date ?? null, crewQuantity: numberValue(row.crew_quantity ?? 1), uomRateSnapshots: row.uom_rate_snapshots ?? {},
    includedHoursSnapshot: numberValue(row.included_hours_snapshot), rtUnits: numberValue(row.rt_units),
    otUnits: numberValue(row.ot_units), dtUnits: numberValue(row.dt_units), rtRate: numberValue(row.rt_rate),
    otRate: numberValue(row.ot_rate), dtRate: numberValue(row.dt_rate), subtotal: numberValue(row.subtotal),
    billedHours: numberValue(row.billed_hours), notes: row.notes ?? null, sortOrder: numberValue(row.sort_order),
    sectionId: row.installation_quote_section_id ?? null, nonPrintable: row.non_printable === true,
    replacesLineId: row.replaces_line_id ?? null, optionIds: [],
  };
}

/** Option ids by line id, from a membership read. */
function optionIdsByLine(rows: InstallationOptionMembershipRow[], lineKey: 'installation_quote_line_id' | 'installation_labor_line_id'): Map<string, string[]> {
  const byLine = new Map<string, string[]>();
  for (const row of rows) {
    const lineId = String(row[lineKey]);
    byLine.set(lineId, [...(byLine.get(lineId) ?? []), String(row.installation_quote_option_id)]);
  }
  return byLine;
}

function mapQuoteVersion(row: any): InstallationQuoteVersion {
  return {
    proposalSubmissionId: row.proposal_submission_id ?? null,
    proposalReviewState: row.proposal_review_state ?? null,
    proposalApprovalCeiling: row.proposal_approval_ceiling == null ? null : numberValue(row.proposal_approval_ceiling),
    id: String(row.installation_quote_version_id),
    originalRevisionOf: row.original_revision_of ?? null, confirmationMethod: row.confirmation_method ?? null, versionNumber: numberValue(row.version_number),
    status: row.status,
    taxMode: row.tax_mode ?? 'legacy', taxRegionCode: row.tax_region_code ?? null, taxAsOf: row.tax_as_of,
    taxTotal: numberValue(row.tax_total), taxContext: row.tax_context ?? {},
    itemTotal: numberValue(row.item_total),
    laborTotal: numberValue(row.labor_total),
    warrantyTotal: numberValue(row.warranty_total),
    total: numberValue(row.total),
    billedHours: numberValue(row.billed_hours),
    changeOrderId: row.installation_change_order_id ?? null,
    acceptedAt: row.accepted_at ?? null,
    acceptedSignatureRecordId: row.accepted_signature_record_id ?? null,
    appliedOptions: mapAppliedOptions(row.applied_options),
    createdAt: String(row.created_at),
  };
}

type AppliedOptionsRow = {
  options?: Array<{ id?: unknown; letter?: unknown; name?: unknown; chosen?: unknown }>;
  lines?: Array<{ grid?: unknown; line_id?: unknown; kind?: unknown; letters?: unknown; replaced?: { description?: unknown; subtotal?: unknown } | null }>;
};

/** installation_quote_versions.applied_options; null before 20261007235500 and without options. */
function mapAppliedOptions(value: unknown): InstallationAppliedOptions | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as AppliedOptionsRow;
  return {
    options: (row.options ?? []).map(option => ({
      id: String(option.id), letter: String(option.letter), name: String(option.name ?? ''), chosen: option.chosen === true,
    })),
    lines: (row.lines ?? []).map(line => ({
      grid: line.grid === 'labor' ? 'labor' : 'items',
      lineId: String(line.line_id),
      kind: line.kind === 'swap' ? 'swap' : 'add',
      letters: Array.isArray(line.letters) ? line.letters.map(String) : [],
      replaced: line.replaced ? { description: String(line.replaced.description ?? ''), subtotal: numberValue(line.replaced.subtotal) } : null,
    })),
  };
}

function mapShift(row: any): InstallationScheduleShift {
  const assignments = Array.isArray(row.labor_schedule_assignments) ? row.labor_schedule_assignments : [];
  return {
    id: String(row.shift_id), roomId: row.installation_room_id ?? null,
    laborLineId: row.installation_labor_line_id ?? null, roleId: row.labor_role_id ?? null,
    laborLineageId: row.labor_lineage_id ?? null, laborPurpose: row.installation_labor_purpose ?? null, workTypeParts: row.installation_work_type_parts ?? null,
    startsAt: String(row.starts_at), endsAt: String(row.ends_at), requiredPeople: numberValue(row.required_people),
    scheduleStatus: row.schedule_status ?? 'planned', publishStatus: row.publish_status === 'published' ? 'published' : 'draft',
    notes: row.notes ?? null, unpaidBreakMinutes: numberValue(row.unpaid_break_minutes ?? row.sling_unpaid_break_minutes), updatedAt: row.updated_at,
    createdAt: row.created_at ?? null, createdByName: row.created_profile?.full_name ?? null,
    updatedByName: row.updated_profile?.full_name ?? null,
    assignments: assignments.map((assignment: any) => ({
      id: String(assignment.assignment_id), profileId: assignment.profile_id ?? null,
      freelancerId: assignment.freelancer_id ?? null, status: assignment.status ?? 'requested',
      name: assignment.assigned_profile?.full_name ?? assignment.freelancers?.name ?? 'Planned person',
    })),
    timeEntryId: row.time_entry_id ?? null,
    plannedStartsAt: row.planned_starts_at ?? null,
    plannedEndsAt: row.planned_ends_at ?? null,
  };
}

export async function fetchInstallationJob(jobId: string, options?: ClientOption): Promise<InstallationJobDetail> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const [jobResult, roomsResult, quotesResult, changeOrdersResult, contactsResult, shiftsResult] = await Promise.all([
    supabase.from('installation_jobs').select('*,clients(business_name,sales_person_profile_id),client_branches(branch_name),venues(venue_name),locations(name),updated_profile:user_profiles!installation_jobs_updated_by_fkey(full_name)').eq('installation_job_id', jobId).single(),
    supabase.from('installation_rooms').select('*').eq('installation_job_id', jobId).order('sort_order'),
    supabase.from('installation_quote_versions').select('*').eq('installation_job_id', jobId).order('version_number', { ascending: false }),
    supabase.from('installation_change_orders').select('*').eq('installation_job_id', jobId).order('sequence_number', { ascending: false }),
    supabase.from('installation_contacts').select(`*,client_contacts(first_name,last_name,email,phone)`).eq('installation_job_id', jobId).order('sort_order'),
    supabase.rpc('installation_schedule_rows', { p_installation_job_id: jobId }),
  ]);
  const primaryFailure = [jobResult, roomsResult, quotesResult, changeOrdersResult, contactsResult, shiftsResult].find(result => result.error)?.error;
  if (primaryFailure) throw primaryFailure;
  const job = jobResult.data as any;
  const quoteRows = (quotesResult.data ?? []) as any[];
  const quoteRow = quoteRows[0];
  if (!quoteRow) throw new Error('The Installation job has no quote version.');
  const linkedLaborIds = [...new Set((shiftsResult.data ?? []).map((row: { installation_labor_line_id: string | null }) => row.installation_labor_line_id).filter(Boolean))];
  const quoteVersionId = String(quoteRow.installation_quote_version_id);
  const [lineResult, laborResult, sectionResult, approvalResult, warrantyResult, linkedLaborResult, optionResult, optionsEnabledResult, groupResult] = await Promise.all([
    readAllPages<Record<string, unknown>>((from, to) => supabase.from('installation_quote_lines').select('*').eq('installation_quote_version_id', quoteVersionId)
      .order('sort_order').order('created_at').order('installation_quote_line_id').range(from, to)),
    readAllPages<Record<string, unknown>>((from, to) => supabase.from('installation_labor_lines').select('*').eq('installation_quote_version_id', quoteVersionId)
      .order('sort_order').order('created_at').order('installation_labor_line_id').range(from, to)),
    supabase.from('installation_quote_sections').select('*').eq('installation_quote_version_id', quoteRow.installation_quote_version_id).order('sort_order'),
    supabase.from('installation_approvals').select(`*,approval_bands(approver_label)`).eq('installation_quote_version_id', quoteRow.installation_quote_version_id).order('band_seq'),
    supabase.from('installation_quote_warranty_options').select('*').eq('installation_quote_version_id', quoteRow.installation_quote_version_id).order('sort_order'),
    quoteRow.copied_from_quote_id && linkedLaborIds.length
      ? supabase.from('installation_labor_lines').select('installation_labor_line_id,lineage_id').in('installation_labor_line_id', linkedLaborIds)
      : Promise.resolve({ data: [], error: null }),
    supabase.from('installation_quote_options').select('*').eq('installation_quote_version_id', quoteVersionId).order('letter'),
    supabase.rpc('installation_quote_options_enabled'),
    supabase.from('installation_quote_option_groups').select('installation_quote_option_group_id,name').eq('installation_quote_version_id', quoteVersionId).limit(1),
  ]);
  const detailFailure = [lineResult, laborResult, approvalResult, warrantyResult, linkedLaborResult].find(result => result.error)?.error;
  if (detailFailure) throw detailFailure;
  // Before 20261007230000 there are no options; the quote is all base lines.
  if (optionResult.error && !isMissingRelationError(optionResult.error)) throw optionResult.error;
  const quoteOptions = (optionResult.error ? [] : (optionResult.data ?? []) as InstallationQuoteOptionRow[]).map(mapQuoteOption);
  // Before 20261014172600 there are no package groups.
  if (groupResult.error && !isMissingRelationError(groupResult.error)) throw groupResult.error;
  const groupRow = (groupResult.error ? [] : groupResult.data ?? [])[0] as { installation_quote_option_group_id: string; name: string } | undefined;
  const optionGroup = groupRow ? { id: String(groupRow.installation_quote_option_group_id), name: String(groupRow.name ?? '') } : null;
  const optionIds = quoteOptions.map(option => option.id);
  const [itemMemberships, laborMemberships] = optionIds.length
    ? await Promise.all([
      readAllPages<InstallationOptionMembershipRow>((from, to) => supabase.from('installation_quote_option_items').select('installation_quote_option_id,installation_quote_line_id')
        .in('installation_quote_option_id', optionIds).order('installation_quote_option_id').order('installation_quote_line_id').range(from, to)),
      readAllPages<InstallationOptionMembershipRow>((from, to) => supabase.from('installation_quote_option_labor').select('installation_quote_option_id,installation_labor_line_id')
        .in('installation_quote_option_id', optionIds).order('installation_quote_option_id').order('installation_labor_line_id').range(from, to)),
    ])
    : [{ data: [], error: null }, { data: [], error: null }];
  const membershipFailure = itemMemberships.error ?? laborMemberships.error;
  if (membershipFailure) throw membershipFailure;
  const itemOptions = optionIdsByLine(itemMemberships.data, 'installation_quote_line_id');
  const laborOptions = optionIdsByLine(laborMemberships.data, 'installation_labor_line_id');
  // An accepted quote's lines that chosen options brought in (since 20261007235500).
  const appliedLines = new Map((mapAppliedOptions(quoteRow.applied_options)?.lines ?? []).map(line => [`${line.grid}:${line.lineId}`, line]));
  const allQuoteLines = lineResult.data.map(row => ({ ...mapQuoteLine(row), optionIds: itemOptions.get(String(row.installation_quote_line_id)) ?? [],
    appliedFrom: appliedLines.get(`items:${String(row.installation_quote_line_id)}`) ?? null }));
  const allLaborLines = laborResult.data.map(row => ({ ...mapLaborLine(row), optionIds: laborOptions.get(String(row.installation_labor_line_id)) ?? [],
    appliedFrom: appliedLines.get(`labor:${String(row.installation_labor_line_id)}`) ?? null }));
  // Before 20261001022039 there is no sections table; the quote simply has no headings yet.
  if (sectionResult.error && !isMissingRelationError(sectionResult.error)) throw sectionResult.error;
  const laborLineages = new Map((linkedLaborResult.data ?? []).map(row => [row.installation_labor_line_id, row.lineage_id]));
  return {
    id: String(job.installation_job_id), code: String(job.job_code), name: String(job.name), status: job.status,
    quoteType: job.quote_type === 'service_agreement' ? 'service_agreement' : 'installation',
    clientId: job.client_id ?? null, clientName: job.clients?.business_name ?? null,
    clientBranchId: job.client_branch_id == null ? null : Number(job.client_branch_id),
    clientBranchName: job.client_branches?.branch_name ?? null,
    venueId: job.venue_id ?? null,
    venueName: job.venues?.venue_name ?? null,
    locationKind: (['client', 'branch', 'venue', 'other'].includes(job.installation_location_kind)
      ? job.installation_location_kind
      : 'client') as InstallationLocationKind,
    siteLocationId: job.site_location_id ?? null, locationName: job.locations?.name ?? null,
    primaryContactId: job.primary_contact_id ?? null, salesProfileId: job.sales_profile_id ?? null,
    customerSalesProfileId: job.clients?.sales_person_profile_id ?? null, createdByProfileId: job.created_by ?? null,
    scopeOfWork: job.scope_of_work ?? '', logisticsNotes: job.logistics_notes ?? '',
    requestedStartDate: job.requested_start_date ?? null,
    address: { line1: job.installation_address_line1 ?? '', line2: job.installation_address_line2 ?? '',
      city: job.installation_city ?? '', state: job.installation_state ?? '', postalCode: job.installation_postal_code ?? '',
      country: job.installation_country ?? '' },
    scheduleUnlocked: job.schedule_unlocked === true, confirmedAt: job.confirmed_at ?? null,
    version: numberValue(job.version), updatedAt: String(job.updated_at), updatedByName: job.updated_profile?.full_name ?? null,
    rooms: ((roomsResult.data ?? []) as any[]).map(row => ({ id: String(row.installation_room_id), name: row.name,
      description: row.description ?? null, sortOrder: numberValue(row.sort_order) })),
    quote: mapQuoteVersion(quoteRow),
    inclusionsExclusions: quoteRow.inclusions_exclusions ?? '',
    selectedWarrantyOptionId: quoteRow.selected_warranty_option_id ?? null,
    warrantyOptions: ((warrantyResult.data ?? []) as any[]).map(row => ({
      id: String(row.installation_quote_warranty_option_id), tierId: String(row.service_agreement_tier_id),
      code: String(row.code_snapshot), name: String(row.name_snapshot), description: row.description_snapshot ?? null,
      price: numberValue(row.price_snapshot), termMonths: numberValue(row.term_months_snapshot),
      pmVisitCount: numberValue(row.pm_visit_count_snapshot), discountPercent: numberValue(row.parts_labor_discount_percent_snapshot),
      responseLevel: row.response_level_snapshot ?? null, onsiteSupport: row.onsite_support_snapshot ?? null,
      manufacturerWarranty: row.manufacturer_warranty_snapshot ?? null, loanerEquipment: row.loaner_equipment_snapshot ?? null,
      included: row.included_by_default === true, sortOrder: numberValue(row.sort_order),
    })),
    quotes: quoteRows.map(mapQuoteVersion),
    changeOrders: ((changeOrdersResult.data ?? []) as any[]).map(row => ({
      id: String(row.installation_change_order_id),
      sequenceNumber: numberValue(row.sequence_number),
      title: String(row.title),
      description: row.description ?? null,
      status: row.status,
      baseTotal: numberValue(row.base_total),
      revisedTotal: numberValue(row.revised_total),
      amountDelta: numberValue(row.amount_delta),
      quoteVersionId: row.installation_quote_version_id ?? null,
      acceptedSignatureRecordId: row.accepted_signature_record_id ?? null,
      createdAt: String(row.created_at),
      updatedAt: String(row.updated_at),
    })),
    quoteLines: allQuoteLines.filter(line => !line.optionIds.length),
    laborLines: allLaborLines.filter(line => !line.optionIds.length),
    quoteOptions,
    optionGroup,
    optionQuoteLines: allQuoteLines.filter(line => line.optionIds.length > 0),
    optionLaborLines: allLaborLines.filter(line => line.optionIds.length > 0),
    optionsEnabled: !optionsEnabledResult.error && optionsEnabledResult.data === true,
    sections: ((sectionResult.error ? [] : sectionResult.data ?? []) as InstallationQuoteSectionRow[]).map(mapSection),
    approvals: ((approvalResult.data ?? []) as any[]).map(row => ({ id: String(row.installation_approval_id), status: row.status,
      proposalSubmissionId: row.proposal_submission_id ?? null,
      bandSequence: row.band_seq == null ? null : numberValue(row.band_seq), approverLabel: row.approval_bands?.approver_label ?? row.approver_role ?? null,
      assignedProfileId: row.assigned_profile_id ?? null, amountBasis: numberValue(row.amount_basis), requestedAt: String(row.requested_at),
      decidedAt: row.decided_at ?? null, decisionNote: row.decision_note ?? null })),
    contacts: ((contactsResult.data ?? []) as any[]).map(row => ({ id: String(row.installation_contact_id),
      clientContactId: row.client_contact_id ?? null, roleLabel: row.role_label ?? null, notes: row.notes ?? null,
      name: [row.client_contacts?.first_name, row.client_contacts?.last_name].filter(Boolean).join(' ') || 'Contact',
      email: row.client_contacts?.email ?? null, phone: row.client_contacts?.phone ?? null })),
    shifts: ((shiftsResult.data ?? []) as any[]).map(row => mapShift({ ...row, labor_lineage_id: laborLineages.get(row.installation_labor_line_id) })),
    deletedAt: job.deleted_at ?? null,
  };
}

export function resolveInstallationShiftLaborLine(
  job: Pick<InstallationJobDetail, 'laborLines'>,
  shift: Pick<InstallationScheduleShift, 'laborLineId' | 'laborLineageId' | 'roleId' | 'roomId'>,
): InstallationLaborLine | null {
  if (!shift.laborLineId) return null;
  const exact = job.laborLines.find(line => line.id === shift.laborLineId);
  if (exact) return exact;
  if (shift.laborLineageId) {
    return job.laborLines.find(line => line.lineageId === shift.laborLineageId) ?? null;
  }

  // Change Orders clone immutable quote labor lines. Existing shifts keep their
  // historical line IDs, so reconcile those read-only links to the new bucket
  // by role and room without rewriting the published schedule.
  const roleMatches = shift.roleId
    ? job.laborLines.filter(line => line.roleId === shift.roleId)
    : [];
  const roomMatches = roleMatches.filter(line => line.roomId === shift.roomId);
  if (roomMatches.length === 1) return roomMatches[0];
  return roleMatches.length === 1 ? roleMatches[0] : null;
}

/** Working hours of one visit: its span less the unpaid break. */
export function installationVisitHours(shift: Pick<InstallationScheduleShift, 'startsAt' | 'endsAt' | 'unpaidBreakMinutes'>): number {
  return Math.max(0, (new Date(shift.endsAt).getTime() - new Date(shift.startsAt).getTime()) / 3_600_000 - (shift.unpaidBreakMinutes ?? 0) / 60);
}

/** Person-hours use the greater of required people and actively planned people. */
export function installationVisitHeadcount(shift: Pick<InstallationScheduleShift, 'requiredPeople' | 'assignments'>): number {
  return Math.max(shift.requiredPeople, shift.assignments.filter(item => !['cancelled', 'declined', 'withdrawn'].includes(item.status)).length);
}

/** Portions are paid seconds of one canonical person entry, never extra shifts. */
export function installationWorkTypeVisitHours(shift: InstallationScheduleShift, workType?: string): number {
  if (shift.timeEntryId && shift.workTypeParts?.length) return shift.workTypeParts
    .filter(part => workType == null || (part.work_type || 'Needs review').toLowerCase() === workType.toLowerCase())
    .reduce((sum, part) => sum + (part.end_seconds == null ? 0 : (Number(part.end_seconds) - Number(part.start_seconds)) / 3600), 0);
  return installationVisitHours(shift) * installationVisitHeadcount(shift);
}

export type InstallationScheduleBucket = InstallationHourSummary & {
  unallocated?: boolean;
  key: string;
  roleId: string | null;
  roomId: string | null;
  /** Active visits in start order; cancelled visits never count toward hours. */
  visits: InstallationScheduleShift[];
  cancelledVisits: InstallationScheduleShift[];
};

/** Purposes combine quoted rooms; legacy unclassified plans still key by role. */
export const installationScheduleBucketKey = (laborLineId: string | null, roleId: string | null, purpose?: string | null) =>
  purpose?.trim() ? `purpose:${purpose.trim().toLowerCase()}` : laborLineId ?? `unquoted:${roleId ?? 'none'}`;

/** Purpose budgets include every quoted room; classification never guesses a room line. */
export function groupInstallationSchedule(job: Pick<InstallationJobDetail, 'laborLines' | 'shifts'>): InstallationScheduleBucket[] {
  const groups = new Map<string, { purpose: string; lines: InstallationLaborLine[]; shifts: InstallationScheduleShift[] }>();
  const group = (key: string, purpose: string) => {
    if (!groups.has(key)) groups.set(key, { purpose, lines: [], shifts: [] });
    return groups.get(key)!;
  };
  job.laborLines.forEach(line => {
    const purpose = line.position.trim() || 'Custom labor';
    group(installationScheduleBucketKey(null, null, purpose), purpose).lines.push(line);
  });
  job.shifts.forEach(shift => {
    if (shift.timeEntryId && shift.workTypeParts?.length) {
      new Set(shift.workTypeParts.map(part => part.work_type?.trim() || '')).forEach(purpose => {
        group(purpose ? installationScheduleBucketKey(null, null, purpose) : 'unallocated:actual', purpose || 'Needs review').shifts.push(shift);
      });
    } else {
      const line = resolveInstallationShiftLaborLine(job, shift);
      const purpose = shift.laborPurpose?.trim() || line?.position.trim();
      const key = purpose ? installationScheduleBucketKey(null, null, purpose) : shift.timeEntryId ? 'unallocated:actual' : installationScheduleBucketKey(null, shift.roleId);
      group(key, purpose || (shift.timeEntryId ? 'Needs review' : 'Unquoted role')).shifts.push(shift);
    }
  });
  return [...groups].map(([key, { purpose, lines, shifts }]) => {
    const visits = shifts.filter(shift => shift.scheduleStatus !== 'cancelled');
    const hours = (recorded: boolean) => visits.filter(shift => Boolean(shift.timeEntryId) === recorded)
      .reduce((sum, shift) => sum + installationWorkTypeVisitHours(shift, purpose), 0);
    const planned = hours(false), actual = hours(true), scheduled = planned + actual;
    const billed = lines.reduce((sum, line) => sum + line.billedHours, 0);
    return { key, laborLineId: lines.length === 1 ? lines[0].id : null, roleId: lines.length === 1 ? lines[0].roleId : shifts[0]?.roleId ?? null,
      roomId: lines.length === 1 ? lines[0].roomId : null, position: purpose, billed, planned, actual, scheduled,
      remaining: billed - scheduled, over: Math.max(0, scheduled - billed), unbilled: lines.length === 0,
      unallocated: key === 'unallocated:actual', visits, cancelledVisits: shifts.filter(shift => shift.scheduleStatus === 'cancelled') };
  });
}

export function summarizeInstallationHours(job: Pick<InstallationJobDetail, 'laborLines' | 'shifts'>): InstallationHourSummary[] {
  return groupInstallationSchedule(job)
    .filter(bucket => !bucket.unbilled || bucket.visits.length > 0)
    .map(({ laborLineId, position, billed, scheduled, planned, actual, remaining, over, unbilled }) => ({ laborLineId, position, billed, scheduled, planned, actual, remaining, over, unbilled }));
}

export async function updateInstallationJob(jobId: string, version: number, patch: Record<string, unknown>, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('installation_jobs').update({ ...patch, version: version + 1, updated_at: new Date().toISOString() })
    .eq('installation_job_id', jobId).eq('version', version).select('installation_job_id');
  if (error) throw error;
  if (!data?.length) throw new Error('This Installation job changed in another session. Refresh and try again.');
}

export async function addInstallationRoom(jobId: string, name: string, description: string, sortOrder: number, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('installation_rooms').insert({ installation_job_id: jobId, name, description: description || null, sort_order: sortOrder }).select('installation_room_id').single();
  if (error) throw error;
  return data.installation_room_id;
}

export async function updateInstallationRoom(roomId: string, patch: { name: string; description?: string | null; sortOrder?: number }, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('installation_rooms').update({
    name: patch.name.trim(),
    ...(patch.description === undefined ? {} : { description: patch.description?.trim() || null }),
    ...(patch.sortOrder == null ? {} : { sort_order: patch.sortOrder }),
    updated_at: new Date().toISOString(),
  }).eq('installation_room_id', roomId);
  if (error) throw error;
}

export async function deleteInstallationRoom(roomId: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('installation_rooms').delete().eq('installation_room_id', roomId).select('installation_room_id');
  if (error) throw error;
  if (!data?.length) throw new Error('This room could not be removed. Refresh the installation and check your edit access.');
}

export async function addInstallationContact(jobId: string, clientContactId: string, roleLabel: string, notes: string, sortOrder: number, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('installation_contacts').upsert({
    installation_job_id: jobId,
    client_contact_id: clientContactId,
    role_label: roleLabel.trim() || null,
    notes: notes.trim() || null,
    sort_order: sortOrder,
  }, { onConflict: 'installation_job_id,client_contact_id' });
  if (error) throw error;
}

export async function deleteInstallationContact(contactId: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.from('installation_contacts').delete().eq('installation_contact_id', contactId);
  if (error) throw error;
}

type QuoteEditTarget = {
  quote_id: string; item_ids: Record<string, string>; labor_ids: Record<string, string>; warranty_ids: Record<string, string>;
  /** Since 20261001022039; absent before it. */
  section_ids?: Record<string, string>;
  /** Since 20261007230000; absent before it. */
  option_ids?: Record<string, string>;
};
const quoteEditRequests = new Map<string, string>();
const quoteEditsInFlight = new Map<string, Promise<QuoteEditTarget>>();
const quoteEditorKeys = new Map<string, string>();

// Only mutation callers prepare an accepted quote. Reads and focus never do.
// Keep the mounted row drafts when this browser creates its own working revision;
// an explicit alternative switch still gets a new workspace identity.
export const installationQuoteEditorKey = (quoteId: string) => quoteEditorKeys.get(quoteId) ?? quoteId;
async function prepareInstallationQuoteEdit(quoteId: string, options?: ClientOption): Promise<QuoteEditTarget> {
  const inFlight = quoteEditsInFlight.get(quoteId);
  if (inFlight) return inFlight;
  if (!quoteEditRequests.has(quoteId)) quoteEditRequests.set(quoteId, crypto.randomUUID());
  const request = rpcOrThrow<QuoteEditTarget>('installation_prepare_quote_edit', {
    p_expected_quote_id: quoteId, p_request_id: quoteEditRequests.get(quoteId),
  }, options).then(target => {
    if (target.quote_id !== quoteId) quoteEditorKeys.set(target.quote_id, installationQuoteEditorKey(quoteId));
    return target;
  }).finally(() => quoteEditsInFlight.delete(quoteId));
  quoteEditsInFlight.set(quoteId, request);
  return request;
}

export async function upsertInstallationQuoteLine(quoteId: string, line: Partial<InstallationQuoteLine> & { description: string; lineType: 'item' | 'charge' }, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const { data, error } = await supabase.rpc('installation_upsert_quote_line', {
    p_installation_quote_version_id: target.quote_id,
    p_line: {
      id: line.id ? target.item_ids[line.id] ?? line.id : null,
      room_id: line.roomId ?? null,
      line_type: line.lineType,
      catalog_item_id: line.catalogItemId ?? null,
      catalog_charge_id: line.catalogChargeId ?? null,
      code: line.code || null,
      description: line.description,
      manufacturer: line.manufacturer || null,
      model_number: line.modelNumber || null,
      // Absent keeps the saved vendor (the catalog add and older callers don't know it).
      ...(line.vendorId !== undefined ? { vendor_id: line.vendorId } : {}),
      quantity: line.quantity ?? 1,
      uom: line.uom || 'each',
      unit_price: line.unitPrice ?? 0,
      ...(line.unitCost !== undefined ? { unit_cost: line.unitCost } : {}),
      ...(line.marginPercent !== undefined ? { margin_percent: line.marginPercent } : {}),
      notes: line.notes || null,
      sort_order: line.sortOrder ?? 0,
    },
  });
  if (error) throw error;
  if (!data) throw new Error('The quote line could not be verified. Refresh the quote before adding it again.');
  return String(data);
}

export async function deleteInstallationQuoteLine(lineId: string, quoteId: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const { data, error } = await supabase.from('installation_quote_lines').delete().eq('installation_quote_version_id', target.quote_id).eq('installation_quote_line_id', target.item_ids[lineId] ?? lineId).select('installation_quote_line_id').maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('The line could not be removed. Refresh the quote and check your edit access.');
}

export async function upsertInstallationLaborLine(quoteId: string, line: Partial<InstallationLaborLine> & { position: string; uom: InstallationLaborUom }, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const includedHoursSnapshot = line.includedHoursSnapshot ?? (line.uom === 'half_day' ? 5 : line.uom === 'full_day' ? 10 : 1);
  const payload = { installation_quote_version_id: target.quote_id, installation_room_id: line.roomId || null,
    labor_role_id: line.roleId || null, position: line.position, uom: line.uom, included_hours_snapshot: includedHoursSnapshot,
    ...(line.workDate !== undefined ? { work_date: line.workDate || null } : {}),
    ...(line.crewQuantity !== undefined ? { crew_quantity: line.crewQuantity } : {}),
    ...(line.uomRateSnapshots !== undefined ? { uom_rate_snapshots: line.uomRateSnapshots } : {}),
    rt_units: line.rtUnits ?? 0, ot_units: line.otUnits ?? 0, dt_units: line.dtUnits ?? 0,
    rt_rate: line.rtRate ?? 0, ot_rate: line.otRate ?? 0, dt_rate: line.dtRate ?? 0,
    notes: line.notes || null, sort_order: line.sortOrder ?? 0 };
  const result = line.id
    ? await supabase.from('installation_labor_lines').update(payload).eq('installation_quote_version_id', target.quote_id).eq('installation_labor_line_id', target.labor_ids[line.id] ?? line.id).select('installation_labor_line_id').single()
    : await supabase.from('installation_labor_lines').insert(payload).select('installation_labor_line_id').single();
  if (result.error) throw result.error;
  return result.data.installation_labor_line_id;
}

export async function deleteInstallationLaborLine(lineId: string, quoteId: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const { data, error } = await supabase.from('installation_labor_lines').delete().eq('installation_quote_version_id', target.quote_id).eq('installation_labor_line_id', target.labor_ids[lineId] ?? lineId).select('installation_labor_line_id').maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('The line could not be removed. Refresh the quote and check your edit access.');
}

/**
 * Saves a heading over a room's items or labor. A new one takes the room and grid; an edit writes
 * only the fields it is given. An accepted quote is first copied into a working revision, as for
 * line edits, and the heading's id is mapped into it.
 */
export async function saveInstallationQuoteSection(quoteId: string, section: Partial<Pick<InstallationQuoteSection, 'label' | 'sortOrder' | 'showSubtotal' | 'customerSummary'>> & {
  id?: string | null; roomId: string | null; grid: InstallationQuoteSection['grid'];
}, options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const fields = {
    ...(section.label !== undefined ? { label: section.label.trim().slice(0, 200) } : {}),
    ...(section.sortOrder !== undefined ? { sort_order: section.sortOrder } : {}),
    ...(section.showSubtotal !== undefined ? { show_subtotal: section.showSubtotal } : {}),
    ...(section.customerSummary !== undefined ? { customer_summary: section.customerSummary?.trim().slice(0, 240) || null } : {}),
  };
  const result = section.id
    ? await supabase.from('installation_quote_sections').update({ ...fields, updated_at: new Date().toISOString() })
      .eq('installation_quote_version_id', target.quote_id).eq('installation_quote_section_id', target.section_ids?.[section.id] ?? section.id)
      .select('installation_quote_section_id').maybeSingle()
    : await supabase.from('installation_quote_sections').insert({
      ...fields, installation_quote_version_id: target.quote_id, installation_room_id: section.roomId || null, grid: section.grid,
    }).select('installation_quote_section_id').maybeSingle();
  if (result.error) throw result.error;
  if (!result.data) throw new Error('The section could not be saved. Refresh the quote and check your edit access.');
  return String(result.data.installation_quote_section_id);
}

/** Deletes a heading. Its lines stay in the room, unsectioned. */
export async function deleteInstallationQuoteSection(quoteId: string, sectionId: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const { data, error } = await supabase.from('installation_quote_sections').delete()
    .eq('installation_quote_version_id', target.quote_id).eq('installation_quote_section_id', target.section_ids?.[sectionId] ?? sectionId)
    .select('installation_quote_section_id').maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('The section could not be removed. Refresh the quote and check your edit access.');
}

/**
 * Moves lines into a section (null takes them out) and/or sets Non-Printable on them. Written straight
 * to the line table: installation_upsert_quote_line never touches these columns, so a row's autosave
 * cannot undo them, and the database keeps every line in a section of its own room and grid.
 */
export async function setInstallationLinePlacement(quoteId: string, grid: InstallationQuoteSection['grid'], lineIds: string[], placement: {
  sectionId?: string | null; nonPrintable?: boolean;
}, options?: ClientOption): Promise<void> {
  if (!lineIds.length) return;
  const supabase = options?.supabase ?? getSupabaseClient();
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const table = grid === 'labor' ? 'installation_labor_lines' : 'installation_quote_lines';
  const idColumn = grid === 'labor' ? 'installation_labor_line_id' : 'installation_quote_line_id';
  const lineMap = grid === 'labor' ? target.labor_ids : target.item_ids;
  const ids = lineIds.map(id => lineMap[id] ?? id);
  const fields = {
    ...(placement.sectionId !== undefined ? { installation_quote_section_id: placement.sectionId ? target.section_ids?.[placement.sectionId] ?? placement.sectionId : null } : {}),
    ...(placement.nonPrintable !== undefined ? { non_printable: placement.nonPrintable } : {}),
  };
  const { data, error } = await supabase.from(table).update(fields).eq('installation_quote_version_id', target.quote_id).in(idColumn, ids).select(idColumn);
  if (error) throw error;
  if ((data ?? []).length !== ids.length) throw new Error('Some lines could not be updated. Refresh the quote and check your edit access.');
}

async function rpcOrThrow<T = unknown>(name: string, params: Record<string, unknown>, options?: ClientOption): Promise<T> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc(name, params);
  if (error) throw error;
  return data as T;
}

export const submitInstallationQuoteForApproval = (jobId: string, options?: ClientOption) =>
  rpcOrThrow<Record<string, unknown>>('installation_submit_quote_for_approval', { p_installation_job_id: jobId }, options);

export const decideInstallationApproval = (approvalId: string, decision: 'approved' | 'rejected', note = '', options?: ClientOption) =>
  rpcOrThrow<string>('installation_decide_approval', { p_installation_approval_id: approvalId, p_decision: decision, p_note: note || null }, options);

export const overrideInstallationApproval = (jobId: string, reason: string, options?: ClientOption) =>
  rpcOrThrow<string>('installation_admin_override_approval', { p_installation_job_id: jobId, p_reason: reason }, options);

/** Sends the quote, or sends it again from Sent; a new link replaces older unused ones. */
export async function sendInstallationForSignature(jobId: string, recipientEmail: string, options?: ClientOption): Promise<{
  token: string; path: string; quoteVersionId: string; expiresAt: string; replacedLinks: number;
}> {
  const data = objectValue<Record<string, unknown>>(await rpcOrThrow('installation_send_for_signature', {
    p_installation_job_id: jobId, p_recipient_email: recipientEmail || null, p_expires_in_days: 14,
  }, options));
  return {
    token: String(data.token), path: String(data.path), quoteVersionId: String(data.quote_version_id),
    expiresAt: String(data.expires_at ?? ''), replacedLinks: Number(data.replaced_links ?? 0),
  };
}

/** The installation's signing links (newest first) with their states, and the customer responses. */
export async function fetchInstallationQuoteLinks(jobId: string, options?: ClientOption): Promise<InstallationQuoteLinksSummary> {
  const data = objectValue<Partial<InstallationQuoteLinksSummary>>(await rpcOrThrow('installation_quote_links', { p_installation_job_id: jobId }, options));
  return { links: data.links ?? [], responses: data.responses ?? [] };
}

/** The signing page runs signed out: its calls use the anonymous client. */
const linkClient = (options?: ClientOption): ClientOption => ({ supabase: options?.supabase ?? publicSupabase });

/** The time signed is the server's; the email the signer types is kept as reported. */
const signatureParams = (signature: SignatureModalValue, signerEmail: string) => ({
  p_signature_method: signature.method,
  p_signature_data: signature.signatureData,
  p_signer_name: signature.signerName,
  p_signer_email: signerEmail.trim() || null,
  p_signature_image_data_url: signature.imageDataUrl,
  p_signature_image_mime_type: signature.imageMimeType,
  p_user_agent: typeof navigator === 'undefined' ? null : navigator.userAgent,
});

export async function fetchInstallationQuoteLink(token: string, options?: ClientOption): Promise<InstallationQuoteLink> {
  return objectValue<InstallationQuoteLink>(await rpcOrThrow('get_installation_quote_link', { p_token: token }, linkClient(options)));
}

export async function fetchInstallationQuoteLinkTerms(token: string, choices: InstallationQuoteChoices, options?: ClientOption): Promise<InstallationQuoteTerms> {
  return objectValue<InstallationQuoteTerms>(await rpcOrThrow('installation_quote_link_terms', { p_token: token, p_choices: choices }, linkClient(options)));
}

/** Signs on a link with the terms the dialog showed. The server refuses terms that differ from its own (PT409). */
export async function acceptInstallationQuoteLink(token: string, terms: InstallationQuoteTerms, signature: SignatureModalValue, signerEmail = '', options?: ClientOption) {
  return objectValue<{ signature_record_id: string; repeated: boolean }>(await rpcOrThrow('accept_installation_quote_public_v2', {
    p_token: token, p_choices: terms.choices, p_expected_total: terms.total, p_acceptance_statement: terms.statement,
    ...signatureParams(signature, signerEmail),
    p_signature_data: { ...signature.signatureData, reviewed_snapshot_hash: terms.snapshot_hash },
  }, linkClient(options)));
}

/** A decline (closes the link) or a request for changes (the link stays open). */
export async function respondToInstallationQuoteLink(token: string, input: InstallationQuoteResponseInput, options?: ClientOption) {
  return objectValue<{ response_id: string; state: InstallationQuoteLinkState; repeated: boolean }>(await rpcOrThrow('respond_installation_quote_public', {
    p_token: token,
    p_response_type: input.type,
    p_decline_reason: input.type === 'decline' ? input.declineReason : null,
    p_message: input.message.trim() || null,
    p_choices: input.type === 'request_changes' ? input.choices : null,
    p_responder_name: input.name.trim(),
    p_responder_email: input.email.trim() || null,
  }, linkClient(options)));
}

/** In person: the terms for a choice (the quote's selected agreement when `choices` is null). */
export async function fetchInstallationInPersonTerms(quoteVersionId: string, choices: InstallationQuoteChoices | null, options?: ClientOption): Promise<InstallationQuoteTerms> {
  return objectValue<InstallationQuoteTerms>(await rpcOrThrow('installation_quote_in_person_terms', {
    p_installation_quote_version_id: quoteVersionId, p_choices: choices,
  }, options));
}

/** In person: signs with the terms the dialog showed; refused if the quote changed since they were read. */
export async function acceptInstallationInPerson(terms: InstallationQuoteTerms, signature: SignatureModalValue, signerEmail = '', options?: ClientOption) {
  return objectValue<{ signature_record_id: string; repeated: boolean }>(await rpcOrThrow('accept_installation_quote_in_person_v2', {
    p_installation_quote_version_id: terms.quote_version_id, p_choices: terms.choices, p_snapshot_hash: terms.snapshot_hash ?? null,
    p_expected_total: terms.total, p_acceptance_statement: terms.statement, ...signatureParams(signature, signerEmail),
  }, options));
}

export const updateInstallationQuoteProposal = async (
  quoteId: string,
  inclusionsExclusions: string,
  offeredTierIds: string[],
  selectedWarrantyOptionId: string | null,
  options?: ClientOption,
) => {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  return rpcOrThrow<Record<string, unknown>>('installation_update_quote_proposal', {
  p_installation_quote_version_id: target.quote_id,
  p_inclusions_exclusions: inclusionsExclusions,
  p_offered_tier_ids: offeredTierIds,
  p_selected_warranty_option_id: selectedWarrantyOptionId ? target.warranty_ids[selectedWarrantyOptionId] ?? selectedWarrantyOptionId : null,
}, options);
};

export async function fetchInstallationQuoteDefaults(options?: ClientOption): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('installation_quote_defaults').select('inclusions_exclusions').eq('settings_id', true).single();
  if (error) throw error;
  return String(data?.inclusions_exclusions ?? '');
}

export const updateInstallationQuoteDefaults = (inclusionsExclusions: string, options?: ClientOption) =>
  rpcOrThrow<string>('installation_update_quote_defaults', { p_inclusions_exclusions: inclusionsExclusions }, options);

export async function fetchInstallationClientQuotePreview(jobId: string, options?: ClientOption): Promise<InstallationCustomerQuotePayload> {
  return objectValue<InstallationCustomerQuotePayload>(await rpcOrThrow('installation_client_quote_preview', { p_installation_job_id: jobId }, options));
}

/** The live quote as the customer would see it with these (unsaved) settings. */
export async function previewInstallationCustomerView(jobId: string, settings: InstallationCustomerViewSettings, options?: ClientOption): Promise<InstallationCustomerQuotePayload> {
  return objectValue<InstallationCustomerQuotePayload>(await rpcOrThrow('installation_customer_view_preview', { p_installation_job_id: jobId, p_settings: settings }, options));
}

export async function fetchInstallationCustomerViewSettings(jobId: string, options?: ClientOption): Promise<InstallationCustomerViewState> {
  return objectValue<InstallationCustomerViewState>(await rpcOrThrow('installation_customer_view_settings', { p_installation_job_id: jobId }, options));
}

/**
 * Saves the installation's own settings; `null` goes back to its location's defaults. Refused with
 * PT409 when someone saved since `expectedRevision` was read.
 */
export async function saveInstallationCustomerViewSettings(jobId: string, settings: InstallationCustomerViewSettings | null, expectedRevision: number, options?: ClientOption): Promise<InstallationCustomerViewState> {
  return objectValue<InstallationCustomerViewState>(await rpcOrThrow('installation_save_customer_view_settings', {
    p_installation_job_id: jobId, p_settings: settings, p_expected_revision: expectedRevision,
  }, options));
}

/** A location's defaults (admins); `null` reads the service location Installations runs from. */
export async function fetchInstallationCustomerViewDefaults(locationId: string | null, options?: ClientOption): Promise<InstallationCustomerViewDefaults> {
  return objectValue<InstallationCustomerViewDefaults>(await rpcOrThrow('installation_customer_view_location_defaults', { p_location_id: locationId }, options));
}

export async function saveInstallationCustomerViewDefaults(locationId: string, settings: InstallationCustomerViewSettings | null, expectedRevision: number, options?: ClientOption): Promise<InstallationCustomerViewDefaults> {
  return objectValue<InstallationCustomerViewDefaults>(await rpcOrThrow('installation_save_customer_view_location_defaults', {
    p_location_id: locationId, p_settings: settings, p_expected_revision: expectedRevision,
  }, options));
}

export const createInstallationChangeOrder = (jobId: string, title: string, description = '', options?: ClientOption) =>
  rpcOrThrow<string>('installation_create_change_order', {
    p_installation_job_id: jobId, p_title: title, p_description: description || null,
  }, options);

export const unlockInstallationSchedule = (jobId: string, options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_unlock_schedule', { p_installation_job_id: jobId }, options);

export type InstallationShiftInput = {
  laborPurpose?: string | null;
  unpaidBreakMinutes?: number;
  id?: string | null;
  roomId?: string | null;
  laborLineId?: string | null;
  roleId?: string | null;
  startsAt: string;
  endsAt: string;
  requiredPeople: number;
  notes?: string;
};

export const saveInstallationVisit = (jobId: string, input: InstallationShiftInput, save: { requestId: string; expectedUpdatedAt: string | null; expectedAssignmentIds?: string[]; profileIds: string[] | null }, options?: ClientOption) =>
  rpcOrThrow<string>('installation_save_visit', {
    p_installation_job_id: jobId, p_request_id: save.requestId,
    p_input: { ...input, expectedUpdatedAt: save.expectedUpdatedAt, expectedAssignmentIds: save.expectedAssignmentIds, profileIds: save.profileIds },
  }, options);

export type InstallationVisitDayInput = InstallationShiftInput & { visitDate: string };

/** Saves one new visit per day in a single transaction: every day is created or none is. */
export const saveInstallationVisits = (jobId: string, days: InstallationVisitDayInput[], save: { requestId: string; profileIds: string[] | null; calendar?: import('./scheduleCalendar').CalendarReview }, options?: ClientOption) =>
  rpcOrThrow<string[]>('installation_save_visits', {
    p_installation_job_id: jobId, p_request_id: save.requestId,
    p_inputs: days.map(day => ({ ...day, id: null, expectedUpdatedAt: null, profileIds: save.profileIds, calendar: save.calendar })),
  }, options);

export type InstallationVisitUpdate = InstallationVisitDayInput & {
  id: string;
  expectedUpdatedAt: string | null;
  /** Every active assignment on the visit when it was loaded; required when `profileIds` is set. */
  expectedAssignmentIds?: string[];
  /** The staff to plan on the visit, or null to leave its crew untouched. */
  profileIds: string[] | null;
};

/** Updates several existing visits in one transaction; one changed-underneath visit rolls every change back. */
export const updateInstallationVisits = (jobId: string, visits: InstallationVisitUpdate[], requestId: string, options?: ClientOption) =>
  rpcOrThrow<string[]>('installation_save_visits', { p_installation_job_id: jobId, p_request_id: requestId, p_inputs: visits }, options);

/** Cancels several visits in one transaction; visits already cancelled are skipped. */
export const cancelInstallationShifts = (shiftIds: string[], reason = '', options?: ClientOption) =>
  rpcOrThrow<number>('installation_cancel_schedule_shifts', { p_shift_ids: shiftIds, p_reason: reason || null }, options);

export const unlockInstallationOriginal = (jobId: string, quoteId: string, reason: string, options?: ClientOption) =>
  rpcOrThrow<string>('installation_unlock_original_quote', { p_installation_job_id: jobId, p_expected_quote_id: quoteId, p_reason: reason }, options);

export type InstallationAlternative = {
  id: string;
  name: string;
  quote_id: string;
  active: boolean;
  quote_status: string;
  total: number;
  updated_at: string;
};

export const fetchInstallationAlternatives = (jobId: string, options?: ClientOption) =>
  rpcOrThrow<InstallationAlternative[]>('installation_list_alternatives', { p_installation_job_id: jobId }, options);

export const openInstallationAlternative = (jobId: string, quoteId: string, requestId: string,
  alternativeId: string | null, name: string | null, options?: ClientOption) =>
  rpcOrThrow<string>('installation_open_alternative', {
    p_installation_job_id: jobId, p_expected_quote_id: quoteId, p_request_id: requestId,
    p_alternative_id: alternativeId, p_name: name,
  }, options);

/**
 * Admin Confirm (or locking a revised original). A quote with customer options needs the options the
 * customer chose (`optionIds`, possibly empty), which are applied as at signing. `planYears` records a
 * selected paid plan signed for longer than its standard term (null keeps the standard term).
 */
export const confirmInstallationQuote = (jobId: string, quoteId: string, reason: string, optionIds: string[] | null = null,
  planYears: number | null = null, options?: ClientOption) =>
  optionIds === null && planYears == null
    ? rpcOrThrow<string>('installation_confirm_quote_override', { p_installation_job_id: jobId, p_expected_quote_id: quoteId, p_reason: reason }, options)
    : rpcOrThrow<string>('installation_confirm_quote_override_v2', {
      p_installation_job_id: jobId, p_expected_quote_id: quoteId, p_reason: reason,
      p_choices: { ...(optionIds === null ? {} : { option_ids: optionIds }), ...(planYears == null ? {} : { warranty_term_years: planYears }) },
    }, options);

export const assignInstallationShift = (shiftId: string, person: { profileId?: string; freelancerId?: string }, options?: ClientOption) =>
  rpcOrThrow<string>('installation_assign_schedule_shift', {
    p_shift_id: shiftId, p_profile_id: person.profileId ?? null, p_freelancer_id: person.freelancerId ?? null,
  }, options);

export const publishInstallationShifts = (shiftIds: string[], options?: ClientOption) =>
  rpcOrThrow<number>('installation_publish_schedule', { p_shift_ids: shiftIds }, options);

export const cancelInstallationShift = (shiftId: string, reason = '', options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_cancel_schedule_shift', { p_shift_id: shiftId, p_reason: reason || null }, options);

export const archiveInstallationJob = (jobId: string, reason: string, options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_archive_job', { p_installation_job_id: jobId, p_reason: reason }, options);

export const transitionInstallationJob = (jobId: string, status: 'in_progress' | 'completed' | 'lost' | 'cancelled', reason = '', options?: ClientOption) =>
  rpcOrThrow<string>('installation_transition_job', {
    p_installation_job_id: jobId,
    p_status: status,
    p_reason: reason || null,
  }, options);

export const restoreInstallationJob = (jobId: string, options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_restore_job', { p_installation_job_id: jobId }, options);

export async function fetchInstallationInvoiceState(jobId: string, options?: ClientOption): Promise<InstallationInvoiceState> {
  const row = objectValue<Record<string, unknown>>(await rpcOrThrow('installation_invoice_state', {
    p_installation_job_id: jobId,
  }, options));
  return {
    invoiceEnabled: row.invoice_enabled === true,
    canManage: row.can_manage === true,
    netsuiteStatus: String(row.netsuite_status ?? 'not_configured'),
    invoiceId: row.invoice_id ? String(row.invoice_id) : null,
    docNo: row.doc_no ? String(row.doc_no) : null,
    invoiceStatus: row.invoice_status ? String(row.invoice_status) : null,
    queueStatus: String(row.queue_status ?? 'not_generated'),
    issueDate: row.issue_date ? String(row.issue_date) : null,
    total: numberValue(row.total),
  };
}

export const generateInstallationInvoice = (jobId: string, post = false, options?: ClientOption) =>
  rpcOrThrow<string>('generate_installation_invoice', { p_installation_job_id: jobId, p_post: post }, options);

export const updateInstallationInvoiceEnabled = (enabled: boolean, options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_update_invoice_enabled', { p_enabled: enabled }, options);

function mapCatalogRecord(row: any, kind: 'item' | 'charge', usageCount: number): InstallationCatalogRecord {
  return {
    id: String(kind === 'item' ? row.installation_catalog_item_id : row.installation_catalog_charge_id),
    kind,
    code: row.item_code ?? row.charge_code ?? null,
    name: String(row.name ?? ''),
    manufacturer: row.manufacturer ?? null,
    modelNumber: row.model_number ?? null,
    description: row.description ?? null,
    defaultUom: row.default_uom ?? 'each',
    defaultUnitPrice: numberValue(row.default_unit_price),
    taxable: kind === 'charge' ? row.taxable !== false : null,
    active: row.active !== false,
    revenueGroup: row.revenue_group ?? null,
    sellGlCode: row.sell_gl_code ?? null,
    netsuiteItemInternalId: row.netsuite_item_internal_id ?? null,
    netsuiteItemExternalId: row.netsuite_item_external_id ?? null,
    usageCount,
    updatedAt: String(row.updated_at),
  };
}

export async function fetchInstallationCatalog(includeArchived = true, options?: ClientOption): Promise<InstallationCatalogRecord[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  let itemsRequest = supabase.from('installation_catalog_items').select('*').order('name');
  let chargesRequest = supabase.from('installation_catalog_charges').select('*').order('name');
  if (!includeArchived) {
    itemsRequest = itemsRequest.eq('active', true);
    chargesRequest = chargesRequest.eq('active', true);
  }
  const [items, charges, usage] = await Promise.all([
    itemsRequest,
    chargesRequest,
    supabase.from('installation_quote_lines').select('installation_catalog_item_id,installation_catalog_charge_id'),
  ]);
  const failure = [items, charges, usage].find(result => result.error)?.error;
  if (failure) throw failure;
  const itemUsage = new Map<string, number>();
  const chargeUsage = new Map<string, number>();
  for (const line of (usage.data ?? []) as any[]) {
    if (line.installation_catalog_item_id) itemUsage.set(line.installation_catalog_item_id, (itemUsage.get(line.installation_catalog_item_id) ?? 0) + 1);
    if (line.installation_catalog_charge_id) chargeUsage.set(line.installation_catalog_charge_id, (chargeUsage.get(line.installation_catalog_charge_id) ?? 0) + 1);
  }
  return [
    ...((items.data ?? []) as any[]).map(row => mapCatalogRecord(row, 'item', itemUsage.get(row.installation_catalog_item_id) ?? 0)),
    ...((charges.data ?? []) as any[]).map(row => mapCatalogRecord(row, 'charge', chargeUsage.get(row.installation_catalog_charge_id) ?? 0)),
  ];
}

export async function saveInstallationCatalogRecord(
  record: Omit<InstallationCatalogRecord, 'usageCount' | 'updatedAt'>,
  options?: ClientOption,
): Promise<string> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const table = record.kind === 'item' ? 'installation_catalog_items' : 'installation_catalog_charges';
  const idColumn = record.kind === 'item' ? 'installation_catalog_item_id' : 'installation_catalog_charge_id';
  const codeColumn = record.kind === 'item' ? 'item_code' : 'charge_code';
  const payload = {
    [codeColumn]: record.code?.trim() || null,
    name: record.name.trim(),
    manufacturer: record.manufacturer?.trim() || null,
    model_number: record.modelNumber?.trim() || null,
    description: record.description?.trim() || null,
    default_uom: record.defaultUom.trim() || 'each',
    default_unit_price: record.defaultUnitPrice,
    ...(record.kind === 'charge' ? { taxable: record.taxable !== false } : {}),
    active: record.active,
    revenue_group: record.revenueGroup?.trim() || null,
    sell_gl_code: record.sellGlCode?.trim() || null,
    netsuite_item_internal_id: record.netsuiteItemInternalId?.trim() || null,
    netsuite_item_external_id: record.netsuiteItemExternalId?.trim() || null,
    updated_at: new Date().toISOString(),
  };
  const result = record.id
    ? await supabase.from(table).update(payload).eq(idColumn, record.id).select(idColumn).single()
    : await supabase.from(table).insert(payload).select(idColumn).single();
  if (result.error) throw result.error;
  return String((result.data as any)[idColumn]);
}

export async function deleteInstallationCatalogRecord(kind: 'item' | 'charge', id: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const table = kind === 'item' ? 'installation_catalog_items' : 'installation_catalog_charges';
  const idColumn = kind === 'item' ? 'installation_catalog_item_id' : 'installation_catalog_charge_id';
  const { error } = await supabase.from(table).delete().eq(idColumn, id);
  if (error) throw error;
}

export type InstallationPhoto = { id: string; roomId: string | null; storagePath: string; bucket: string; caption: string | null; section: string; createdAt: string; url: string | null };

export async function fetchInstallationPhotos(jobId: string, options?: ClientOption): Promise<InstallationPhoto[]> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('installation_photos').select('*').eq('installation_job_id', jobId).is('deleted_at', null).order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as any[]).map(row => ({ id: String(row.installation_photo_id), roomId: row.installation_room_id ?? null,
    storagePath: String(row.storage_path), bucket: row.storage_bucket ?? 'photos', caption: row.caption ?? null, section: row.section ?? 'general', createdAt: String(row.created_at),
    url: row.storage_bucket === 'job-photos' ? null : supabase.storage.from('photos').getPublicUrl(String(row.storage_path)).data.publicUrl ?? null }));
}

export async function uploadInstallationPhoto(jobId: string, file: File, caption: string, roomId: string | null, options?: ClientOption & { section?: string }): Promise<void> {
  const section = options?.section ?? 'general';
  if (!['general', 'site', 'progress', 'completed'].includes(section)) throw new Error('Choose a photo category.');
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.');
  if (file.size > 25 * 1024 * 1024) throw new Error('Photos must be 25 MB or smaller.');
  const supabase = options?.supabase ?? getSupabaseClient();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `installations/${jobId}/${crypto.randomUUID()}-${safeName}`;
  const upload = await supabase.storage.from('photos').upload(storagePath, file, { contentType: file.type, upsert: false });
  if (upload.error) throw upload.error;
  const created = await supabase.from('installation_photos').insert({ installation_job_id: jobId,
    installation_room_id: roomId, storage_path: storagePath, caption: caption || null, section });
  if (created.error) {
    await supabase.storage.from('photos').remove([storagePath]);
    throw created.error;
  }
}

export async function softDeleteInstallationPhoto(photoId: string, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('installation_photos')
    .update({ deleted_at: new Date().toISOString() })
    .eq('installation_photo_id', photoId);
  if (error) throw error;
}

export async function updateInstallationPhotoCaption(photoId: string, caption: string | null, options?: ClientOption): Promise<void> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('installation_photos')
    .update({ caption: caption?.trim() || null })
    .eq('installation_photo_id', photoId);
  if (error) throw error;
}

export async function updateInstallationPhotoDetails(photoId: string, details: { caption?: string | null; section: string; roomId: string | null }, options?: ClientOption): Promise<void> {
  if (!['general', 'site', 'progress', 'completed'].includes(details.section)) throw new Error('Choose a photo category.');
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.from('installation_photos').update({
    ...(details.caption !== undefined ? { caption: details.caption?.trim() || null } : {}), section: details.section, installation_room_id: details.roomId,
  }).eq('installation_photo_id', photoId).select('installation_photo_id').maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Photo changes were not confirmed. Refresh the photo and try again.');
}

/** Whether customer options are turned on (installation_settings.quote_options_enabled). */
export const fetchInstallationQuoteOptionsEnabled = async (options?: ClientOption) =>
  (await rpcOrThrow<boolean>('installation_quote_options_enabled', {}, options)) === true;

/** Customer options are turned on (installation_settings.quote_options_enabled). Admins only may change it. */
export const updateInstallationQuoteOptionsEnabled = (enabled: boolean, options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_update_quote_options_enabled', { p_enabled: enabled }, options);

/** Whether Send quote can email the signing link (installation_settings.quote_email_enabled). */
export const fetchInstallationQuoteEmailEnabled = async (options?: ClientOption) =>
  (await rpcOrThrow<boolean>('installation_quote_email_enabled', {}, options)) === true;

/** Turns quote emails on or off for every installation. Admins only may change it. */
export const updateInstallationQuoteEmailEnabled = (enabled: boolean, options?: ClientOption) =>
  rpcOrThrow<boolean>('installation_update_quote_email_enabled', { p_enabled: enabled }, options);

/** Creates an option (the next free letter) or renames one; any change returns an approved quote to Draft. */
export async function saveInstallationQuoteOption(
  quoteId: string,
  option: { id?: string | null; name: string; description?: string | null; recommended?: boolean },
  options?: ClientOption,
): Promise<InstallationQuoteOption> {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  return mapQuoteOption(objectValue<InstallationQuoteOptionRow>(await rpcOrThrow('installation_save_quote_option', {
    p_quote_id: target.quote_id,
    p_option: {
      id: option.id ? target.option_ids?.[option.id] ?? option.id : null,
      name: option.name,
      description: option.description ?? null,
      recommended: option.recommended ?? false,
    },
  }, options)));
}

/**
 * Makes or changes the quote's package group (Phase 7): these options, plus, with `baseName`, a package with no lines
 * for the quote as built, marked recommended. Options left out become add-ons again.
 */
export async function saveInstallationOptionGroup(quoteId: string, group: { name: string; optionIds: string[]; baseName?: string | null }, options?: ClientOption) {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  return rpcOrThrow<{ group_id: string; option_ids: string[] }>('installation_save_option_group', {
    p_quote_id: target.quote_id,
    p_name: group.name,
    p_option_ids: group.optionIds.map(id => target.option_ids?.[id] ?? id),
    p_base_name: group.baseName?.trim() || null,
  }, options);
}

/** Ungroups the packages: every option becomes an any-mix add-on again. */
export async function deleteInstallationOptionGroup(quoteId: string, options?: ClientOption) {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  return rpcOrThrow<boolean>('installation_delete_option_group', { p_quote_id: target.quote_id }, options);
}

/** Deletes an option and the lines only it holds; lines shared with another option stay there. */
export async function deleteInstallationQuoteOption(quoteId: string, optionId: string, options?: ClientOption): Promise<void> {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  await rpcOrThrow('installation_delete_quote_option', {
    p_quote_id: target.quote_id, p_option_id: target.option_ids?.[optionId] ?? optionId,
  }, options);
}

/**
 * Sets the whole option set of each line. An empty set returns an added line to the base quote; a swap
 * left with no option is deleted.
 */
export async function setInstallationLineOptions(
  quoteId: string,
  grid: 'items' | 'labor',
  lineIds: string[],
  optionIds: string[],
  options?: ClientOption,
): Promise<void> {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const lineMap = grid === 'labor' ? target.labor_ids : target.item_ids;
  await rpcOrThrow('installation_set_line_options', {
    p_quote_id: target.quote_id,
    p_grid: grid,
    p_line_ids: lineIds.map(id => lineMap[id] ?? id),
    p_option_ids: optionIds.map(id => target.option_ids?.[id] ?? id),
  }, options);
}

/** Copies lines into options, as swaps of them or as added lines. Returns {source line id: new line id}. */
export async function copyInstallationLinesToOptions(
  quoteId: string,
  grid: 'items' | 'labor',
  lineIds: string[],
  optionIds: string[],
  swap: boolean,
  options?: ClientOption,
): Promise<Record<string, string>> {
  const target = await prepareInstallationQuoteEdit(quoteId, options);
  const lineMap = grid === 'labor' ? target.labor_ids : target.item_ids;
  return objectValue<Record<string, string>>(await rpcOrThrow('installation_copy_lines_to_options', {
    p_quote_id: target.quote_id,
    p_grid: grid,
    p_line_ids: lineIds.map(id => lineMap[id] ?? id),
    p_option_ids: optionIds.map(id => target.option_ids?.[id] ?? id),
    p_swap: swap,
  }, options));
}

export function installationErrorMessage(error: unknown, fallback = 'Installation action failed.'): string {
  const message = (error as PostgrestError | Error | null)?.message?.trim();
  if (!message) return fallback;
  if (/42501|permission|not accessible|access is not available/i.test(message)) return 'You do not have access to this Installation action.';
  if (/installation_alternative_name_idx/i.test(message)) return 'That version name already exists. Choose a different name.';
  if (/immutable/i.test(message)) return 'This quote is preserved. Choose Edit items and labor or create a new version.';
  return message;
}

export const updateInstallationTaxSettings = async (quoteId: string, region: string, asOf: string, replaceTaxLineIds: string[]) => {
  const target = await prepareInstallationQuoteEdit(quoteId);
  return rpcOrThrow('installation_update_tax_settings', { p_quote_id: target.quote_id, p_region: region || null, p_as_of: asOf,
    p_replace_tax_line_ids: replaceTaxLineIds.map(id => target.item_ids[id] ?? id) });
};
export const saveInstallationLogisticsCharge = async (quoteId: string, type: string, details: Record<string, unknown> | null) => {
  const target = await prepareInstallationQuoteEdit(quoteId);
  return rpcOrThrow<string>('installation_save_logistics_charge', { p_quote_id: target.quote_id, p_type: type, p_details: details });
};

/** Bounded, location-scoped choices for the Schedule shift editor. */
export type InstallationScheduleOption = { id: string; label: string; code: string; name: string; status: string; requestedStartDate: string | null };
export async function fetchInstallationScheduleOptions(locationId: string | null, search = '', options?: ClientOption): Promise<InstallationScheduleOption[]> {
  if (!locationId) return [];
  const supabase = options?.supabase ?? getSupabaseClient();
  const baseQuery = () => supabase.from('installation_jobs')
    .select('installation_job_id,job_code,name,status,requested_start_date')
    .is('deleted_at', null).eq('schedule_unlocked', true)
    .not('status', 'in', '(completed,lost,cancelled)')
    .eq('site_location_id', locationId);
  const term = search.trim();
  const results = term
    ? await Promise.all([
      baseQuery().ilike('name', `%${term}%`).order('job_code', { ascending: false }).limit(100),
      baseQuery().ilike('job_code', `%${term}%`).order('job_code', { ascending: false }).limit(100),
    ])
    : [await baseQuery().order('job_code', { ascending: false }).limit(100)];
  for (const result of results) if (result.error) throw result.error;
  const rows = new Map<string, InstallationScheduleOption>();
  for (const result of results) for (const row of result.data ?? []) {
    const id = String(row.installation_job_id);
    rows.set(id, {
      id,
      label: `${row.job_code} · ${row.name}`,
      code: row.job_code,
      name: row.name,
      status: row.status,
      requestedStartDate: row.requested_start_date,
    });
  }
  return Array.from(rows.values()).sort((a, b) => b.code.localeCompare(a.code)).slice(0, 100);
}
